/**
 * trainSummaryPdf.ts - Native PDF report for a Train Summary scenario.
 *
 *   - Reynolds & Bauhm CONFIDENTIAL diagonal watermark on every page
 *   - Cover page with scenario meta + train info
 *   - Reservoir input parameters
 *   - Step-by-step computations per pipeline stage with the equations used
 *   - Required-improvements digest
 *   - Modules in train with references
 *
 * Public API: generateTrainSummaryPDF(opts)
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { s } from './reservoirPdf';
import type { ModuleTrain } from './moduleTrains';
import { MODULES } from './moduleRegistry';
import { getModuleDoc } from './moduleDocs';
import { assessReservoirHealth } from './reservoirHealth';
import {
  REGULATORY_THRESHOLDS, CALIBRATION_BOUNDS, PLATFORM_LIMITATIONS,
  VERIFICATION_STATUS, REPRODUCIBILITY, CONFLICT_DISCLOSURES,
  DEFENSIBILITY_HEADLINE,
} from './defensibility';

export interface ScenarioParams {
  area_m2: number; depth_mean_m: number; depth_max_m: number;
  chl_ugL: number; nom_mgL: number; tn_mgL: number; tp_mgL: number;
  pH: number; surfaceTempC: number; hypoTempC: number;
  stratified: boolean; fCyano: number; windMs: number;
  doSurface_mgL: number; doHypo_mgL: number; orp_mV: number;
  N_brunt: number; feII_mgL: number; mnII_mgL: number;
  diffuserQair_Ls: number; diffuserDepth_m: number; bubble_d_mm: number;
}

export interface PdfResultRow {
  label: string;
  value: string;
  unit?: string;
  status: 'ok' | 'warn' | 'critical' | 'na';
  note?: string;
  improvement?: string;
}

export interface PdfStageBlock {
  stage: string;
  rows: PdfResultRow[];
}

export type ReportMode = 'pre-nda' | 'full';

export interface GenerateTrainSummaryPDFOptions {
  scenarioName: string;
  scenarioSource: 'preset' | 'benchmark' | 'perfect' | 'custom' | 'default';
  train: ModuleTrain;
  params: ScenarioParams;
  blocks: PdfStageBlock[];
  enabledModuleIds: string[];
  /** Optional: modules placed on the bathymetric map. */
  placedModules?: Array<{ x_m: number; y_m: number; depth_m: number }>;
  /**
   * 'pre-nda' - results-and-data sections only (Sections 1, 2, 3, 3A-3D, 10, 11)
   * 'full'    - complete report including post-NDA methodology (Sections 4-9)
   */
  reportMode?: ReportMode;
}

// ----------
// Layout constants
// ----------
const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN_X = 18;
const MARGIN_TOP = 25;     // 14mm blue band + 5mm chip strip + 6mm gap = 25mm
const MARGIN_BOTTOM = 18;
const CONTENT_W = PAGE_W - 2 * MARGIN_X;

const COLOUR = {
  primary: [13, 71, 161] as [number, number, number],          // R&B blue
  ink:     [25, 32, 45] as [number, number, number],
  muted:   [110, 120, 134] as [number, number, number],
  ok:      [21, 128, 61] as [number, number, number],
  warn:    [202, 138, 4] as [number, number, number],
  crit:    [185, 28, 28] as [number, number, number],
  light:   [241, 245, 249] as [number, number, number],
  divider: [205, 213, 226] as [number, number, number],
  brand:   [220, 38, 38] as [number, number, number],          // watermark red
};

const FMT_INPUT_LABELS: Record<keyof ScenarioParams, { label: string; unit: string; precision: number }> = {
  area_m2:           { label: 'Surface area',          unit: 'm-2',    precision: 0 },
  depth_mean_m:      { label: 'Mean depth',            unit: 'm',     precision: 2 },
  depth_max_m:       { label: 'Max depth',             unit: 'm',     precision: 1 },
  chl_ugL:           { label: 'Chlorophyll-a',         unit: '-ug/L',  precision: 1 },
  nom_mgL:           { label: 'NOM / DOC',             unit: 'mg/L',  precision: 1 },
  tn_mgL:            { label: 'Total nitrogen',        unit: 'mg/L',  precision: 2 },
  tp_mgL:            { label: 'Total phosphorus',      unit: 'mg/L',  precision: 3 },
  pH:                { label: 'pH',                    unit: '',      precision: 1 },
  surfaceTempC:      { label: 'Surface temperature',   unit: ' degC',    precision: 1 },
  hypoTempC:         { label: 'Hypolimnion temperature', unit: ' degC',  precision: 1 },
  stratified:        { label: 'Stratified',            unit: '',      precision: 0 },
  fCyano:            { label: 'Cyanobacteria fraction', unit: '',     precision: 2 },
  windMs:            { label: '10-m wind',             unit: 'm/s',   precision: 1 },
  doSurface_mgL:     { label: 'DO at surface',         unit: 'mg/L',  precision: 1 },
  doHypo_mgL:        { label: 'DO at hypolimnion',     unit: 'mg/L',  precision: 2 },
  orp_mV:            { label: 'ORP at sediment',       unit: 'mV',    precision: 0 },
  N_brunt:           { label: 'Brunt-V--is--l-- N',       unit: '1/s',   precision: 3 },
  feII_mgL:          { label: 'Fe(II) hypolimnion',    unit: 'mg/L',  precision: 2 },
  mnII_mgL:          { label: 'Mn(II) hypolimnion',    unit: 'mg/L',  precision: 2 },
  diffuserQair_Ls:   { label: 'Air per diffuser',      unit: 'L/s',   precision: 2 },
  diffuserDepth_m:   { label: 'Diffuser release depth', unit: 'm',    precision: 1 },
  bubble_d_mm:       { label: 'Bubble diameter d---',    unit: 'mm',    precision: 1 },
};

// ----------
// Header / footer / watermark
// ----------

type NdaTier = 'pre-nda' | 'post-nda';

function drawWatermark(doc: jsPDF, tier: NdaTier) {
  doc.saveGraphicsState();
  // jsPDF v2 exposes setGState; cast to any to bypass type definitions in older builds
  const dAny = doc as unknown as { setGState?: (gs: unknown) => void; GState?: new (opts: { opacity?: number }) => unknown };
  if (dAny.setGState && dAny.GState) {
    dAny.setGState(new dAny.GState({ opacity: tier === 'post-nda' ? 0.10 : 0.08 }));
  }
  // Primary watermark
  doc.setTextColor(COLOUR.brand[0], COLOUR.brand[1], COLOUR.brand[2]);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(54);
  const cx = PAGE_W / 2;
  const cy = PAGE_H / 2;
  doc.text(s('REYNOLDS & BAUHM'), cx, cy - 10, { angle: 35, align: 'center' });
  doc.setFontSize(32);
  doc.text(s('CONFIDENTIAL'), cx, cy + 6, { angle: 35, align: 'center' });
  // Post-NDA additional stamp
  if (tier === 'post-nda') {
    doc.setFontSize(22);
    doc.text(s('POST-NDA - NDA REQUIRED TO SHARE'), cx, cy + 22, { angle: 35, align: 'center' });
  }
  doc.restoreGraphicsState();
  doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);
}

function drawHeader(doc: jsPDF, opts: GenerateTrainSummaryPDFOptions, tier: NdaTier, healthScore: number, healthStatus: string) {
  // -------- BAND 1: Blue masthead (y = 0-14) --------
  doc.setFillColor(COLOUR.primary[0], COLOUR.primary[1], COLOUR.primary[2]);
  doc.rect(0, 0, PAGE_W, 14, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(s('Reynolds & Bauhm Smart Water'), MARGIN_X, 8);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(s('Reservoir Aeration Assessment - CONFIDENTIAL'), MARGIN_X, 12);

  // Right side of blue band: scenario name + page-number placeholder (no chips here)
  doc.setFont('helvetica', 'normal');
  const shortName = opts.scenarioName.length > 38 ? opts.scenarioName.slice(0, 36) + '...' : opts.scenarioName;
  doc.text(s(shortName), PAGE_W - MARGIN_X, 8, { align: 'right' });
  doc.text(s('Page ___ of ___'), PAGE_W - MARGIN_X, 12, { align: 'right' });

  // -------- BAND 2: Light strip with NDA + Health chips (y = 14-19) --------
  doc.setFillColor(241, 245, 249);            // very light grey-blue
  doc.rect(0, 14, PAGE_W, 5, 'F');
  doc.setDrawColor(205, 213, 226);
  doc.setLineWidth(0.2);
  doc.line(0, 19, PAGE_W, 19);

  // NDA chip (left of band) - green PRE-NDA or red POST-NDA, with comfortable spacing
  const ndaText = tier === 'post-nda' ? 'POST-NDA' : 'PRE-NDA';
  const ndaColour: [number, number, number] = tier === 'post-nda' ? [185, 28, 28] : [21, 128, 61];
  doc.setFillColor(ndaColour[0], ndaColour[1], ndaColour[2]);
  doc.roundedRect(MARGIN_X, 14.7, 22, 3.8, 0.8, 0.8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.text(s(ndaText), MARGIN_X + 11, 17.2, { align: 'center' });

  // Tier description text (after chip, with gap)
  doc.setTextColor(COLOUR.muted[0], COLOUR.muted[1], COLOUR.muted[2]);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(6.5);
  const ndaDesc = tier === 'post-nda'
    ? 'Methodology section - requires Non-Disclosure Agreement to share'
    : 'Results / data section - share permitted with named recipient only';
  doc.text(s(ndaDesc), MARGIN_X + 25, 17.2);

  // Health chip (right of band) - value + status
  const healthColour: [number, number, number] =
    healthStatus === 'HEALTHY' ? [21, 128, 61] :
    healthStatus === 'AT RISK' ? [202, 138, 4] : [185, 28, 28];
  doc.setFillColor(healthColour[0], healthColour[1], healthColour[2]);
  doc.roundedRect(PAGE_W - MARGIN_X - 50, 14.7, 50, 3.8, 0.8, 0.8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.text(s(`RESERVOIR HEALTH:  ${healthScore.toFixed(0)} / 100   ${healthStatus}`),
    PAGE_W - MARGIN_X - 25, 17.2, { align: 'center' });

  doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);
}

function drawFooter(doc: jsPDF, docNumber: string) {
  const y = PAGE_H - 9;
  doc.setDrawColor(COLOUR.divider[0], COLOUR.divider[1], COLOUR.divider[2]);
  doc.setLineWidth(0.2);
  doc.line(MARGIN_X, y - 4, PAGE_W - MARGIN_X, y - 4);
  // Row 1: confidentiality on the left, generated timestamp on the right
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7);
  doc.setTextColor(COLOUR.muted[0], COLOUR.muted[1], COLOUR.muted[2]);
  doc.text(s('Reynolds & Bauhm - Confidential. Not for external distribution without written consent.'), MARGIN_X, y);
  doc.text(s(`Generated ${new Date().toLocaleString()}`), PAGE_W - MARGIN_X, y, { align: 'right' });
  // Row 2: software identifier (left), document number (centre)
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(6.5);
  doc.setTextColor(COLOUR.muted[0], COLOUR.muted[1], COLOUR.muted[2]);
  doc.text(s('Reynolds & Bauhm Smart Water - Reservoir Suite V 2.0'), MARGIN_X, y + 4);
  doc.setFont('courier', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(COLOUR.primary[0], COLOUR.primary[1], COLOUR.primary[2]);
  doc.text(s(`Document No.  ${docNumber}`), PAGE_W / 2, y + 4, { align: 'center' });
  doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);
}

/** Per-render context tracked across pages. */
interface DocCtx {
  y: number;
  pageNum: number;
  /** Current NDA tier - switched as sections begin. */
  tier: NdaTier;
  /** Bed/overall health score for the header chip. */
  healthScore: number;
  /** Short label (e.g. HEALTHY / AT RISK / CRITICAL) for the header chip. */
  healthStatus: string;
  /** Document reference number, stamped on every page footer. */
  docNumber: string;
}

function decoratePage(doc: jsPDF, opts: GenerateTrainSummaryPDFOptions, ctx: DocCtx) {
  drawWatermark(doc, ctx.tier);
  drawHeader(doc, opts, ctx.tier, ctx.healthScore, ctx.healthStatus);
  drawFooter(doc, ctx.docNumber);
}

function ensureRoom(doc: jsPDF, ctx: DocCtx, opts: GenerateTrainSummaryPDFOptions, needed: number) {
  if (ctx.y + needed > PAGE_H - MARGIN_BOTTOM) {
    doc.addPage();
    ctx.pageNum += 1;
    decoratePage(doc, opts, ctx);
    ctx.y = MARGIN_TOP;
  }
}

function H1(doc: jsPDF, text: string, ctx: DocCtx, opts: GenerateTrainSummaryPDFOptions) {
  // Force a fresh page if H1 wouldn't have room for its title + first paragraph
  ensureRoom(doc, ctx, opts, 40);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(COLOUR.primary[0], COLOUR.primary[1], COLOUR.primary[2]);
  doc.text(s(text), MARGIN_X, ctx.y);
  doc.setDrawColor(COLOUR.primary[0], COLOUR.primary[1], COLOUR.primary[2]);
  doc.setLineWidth(0.6);
  doc.line(MARGIN_X, ctx.y + 1.5, MARGIN_X + 50, ctx.y + 1.5);
  ctx.y += 8;
  doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);
}

function H2(doc: jsPDF, text: string, ctx: DocCtx, opts: GenerateTrainSummaryPDFOptions) {
  // Reserve enough room for the header itself + several lines of likely body
  // content underneath (paragraph + start of table). Stops orphan headers
  // appearing at the bottom of a page with their content on the next page.
  ensureRoom(doc, ctx, opts, 30);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11.5);
  doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);
  doc.text(s(text), MARGIN_X, ctx.y);
  ctx.y += 6;
}

function P(doc: jsPDF, text: string, ctx: DocCtx, opts: GenerateTrainSummaryPDFOptions, opts2?: { size?: number; italic?: boolean; colour?: [number, number, number] }) {
  const size = opts2?.size ?? 9.5;
  doc.setFont('helvetica', opts2?.italic ? 'italic' : 'normal');
  doc.setFontSize(size);
  const c = opts2?.colour ?? COLOUR.ink;
  doc.setTextColor(c[0], c[1], c[2]);
  const lines = doc.splitTextToSize(s(text), CONTENT_W);
  const h = lines.length * (size * 0.42);
  ensureRoom(doc, ctx, opts, h + 2);
  doc.text(lines, MARGIN_X, ctx.y);
  ctx.y += h + 2;
  doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);
}

function statusText(st: PdfResultRow['status']): { label: string; colour: [number, number, number] } {
  switch (st) {
    case 'ok':       return { label: 'OK',       colour: COLOUR.ok };
    case 'warn':     return { label: 'WARN',     colour: COLOUR.warn };
    case 'critical': return { label: 'CRITICAL', colour: COLOUR.crit };
    default:         return { label: 'N/A',      colour: COLOUR.muted };
  }
}

// ----------
// Main generator
// ----------

/**
 * Build a deterministic document number for a given scenario + timestamp.
 * Format: RB-RA-YYYYMMDD-HHMM-XXXX  where XXXX is a 4-character checksum hash
 * of the scenario name + parameters.  Same scenario generated at the same
 * minute will get the same code, which aids traceability.
 */
function buildDocumentNumber(opts: GenerateTrainSummaryPDFOptions, now: Date): string {
  const ymd = now.toISOString().slice(0, 10).replace(/-/g, '');
  const hm = String(now.getHours()).padStart(2, '0') + String(now.getMinutes()).padStart(2, '0');
  // Deterministic 4-char hash of the scenario meta + key parameters
  const seed = JSON.stringify({
    n: opts.scenarioName,
    t: opts.train.id,
    p: [opts.params.area_m2, opts.params.depth_mean_m, opts.params.chl_ugL,
        opts.params.tp_mgL, opts.params.fCyano, opts.params.diffuserQair_Ls],
  });
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = ((h << 5) - h) + seed.charCodeAt(i);
    h = h | 0;
  }
  const code = (Math.abs(h) % 0xFFFFF).toString(36).toUpperCase().padStart(4, '0').slice(0, 4);
  return `RB-RA-${ymd}-${hm}-${code}`;
}

export async function generateTrainSummaryPDF(opts: GenerateTrainSummaryPDFOptions): Promise<void> {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const healthPre = assessReservoirHealth(opts.params);
  const healthLabel0 = healthPre.overallStatus === 'ok' ? 'HEALTHY'
                     : healthPre.overallStatus === 'warn' ? 'AT RISK'
                     : 'CRITICAL';
  const issueDate = new Date();
  const docNumber = buildDocumentNumber(opts, issueDate);
  const _isPreNda = (opts.reportMode ?? 'full') === 'pre-nda';
  const _sensitiveLabels=['Effective OTE','Surface mixing coverage','Plume footprint','Stratification breakdown','Bubble release diameter','per kg O2','O2 delivered per bubble','Detrainment height','Coverage proxy','Cost per kg'];
  const _redactValue=(label,value)=>{if(!_isPreNda)return value;for(let i=0;i<_sensitiveLabels.length;i++)if((label||'').indexOf(_sensitiveLabels[i])>=0)return '--- (post-NDA)';return value;};
  const reportMode: ReportMode = opts.reportMode ?? 'full';
  const modeLabel = reportMode === 'pre-nda' ? 'Pre-NDA Preview' : 'Full Report';

  // ----- PDF document metadata (Reynolds & Bauhm Ltd, Jacek Jeziorny) -----
  doc.setProperties({
    title: `Reservoir Aeration Assessment - ${modeLabel} - ${opts.scenarioName}`,
    subject: 'Reservoir Aeration Engineering Assessment - Confidential',
    author: 'Jacek Jeziorny, Reynolds & Bauhm Ltd',
    keywords: [
      'Reynolds & Bauhm', 'Reservoir Aeration', 'Engineering Assessment',
      'Confidential', 'Jacek Jeziorny', docNumber,
      opts.scenarioName, modeLabel,
    ].join(', '),
    creator: 'Reynolds & Bauhm Smart Water platform',
  });
  // Older jsPDF versions surface the Producer field via the underlying info dict
  const dAny = doc as unknown as { setProducer?: (p: string) => void };
  if (typeof dAny.setProducer === 'function') {
    dAny.setProducer('Reynolds & Bauhm Ltd - Smart Water platform');
  }

  const ctx: DocCtx = {
    y: MARGIN_TOP, pageNum: 1,
    tier: 'pre-nda',
    healthScore: healthPre.overallScore,
    healthStatus: healthLabel0,
    docNumber,
  };

  // ---------- COVER PAGE ----------
  decoratePage(doc, opts, ctx);

  // Big title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(26);
  doc.setTextColor(COLOUR.primary[0], COLOUR.primary[1], COLOUR.primary[2]);
  doc.text(s('Reservoir Aeration'), MARGIN_X, 46);
  doc.text(s('Assessment Report'), MARGIN_X, 58);

  // Decorative bar
  doc.setFillColor(COLOUR.primary[0], COLOUR.primary[1], COLOUR.primary[2]);
  doc.rect(MARGIN_X, 63, 80, 1.5, 'F');

  // Prominent document-number badge in the top-right corner of the cover page
  const docBadgeW = 76;
  const docBadgeH = 18;
  const docBadgeX = PAGE_W - MARGIN_X - docBadgeW;
  const docBadgeY = 36;
  doc.setFillColor(COLOUR.primary[0], COLOUR.primary[1], COLOUR.primary[2]);
  doc.roundedRect(docBadgeX, docBadgeY, docBadgeW, docBadgeH, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text(s('DOCUMENT NUMBER'), docBadgeX + docBadgeW / 2, docBadgeY + 6, { align: 'center' });
  doc.setFontSize(12);
  doc.setFont('courier', 'bold');
  doc.text(s(docNumber), docBadgeX + docBadgeW / 2, docBadgeY + 13, { align: 'center' });
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(6.5);
  doc.text(s('Quote this number in all correspondence'), docBadgeX + docBadgeW / 2, docBadgeY + 16.5, { align: 'center' });
  doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);

  // Subject line
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(11);
  doc.setTextColor(COLOUR.muted[0], COLOUR.muted[1], COLOUR.muted[2]);
  doc.text(s('Prepared for the named recipient under conditions of strict confidentiality'), MARGIN_X, 72);

  // Report-mode banner - large coloured chip under the subject line
  const modeChipColour: [number, number, number] = reportMode === 'pre-nda'
    ? [21, 128, 61]    // emerald - preview
    : [185, 28, 28];   // red - full / post-NDA
  doc.setFillColor(modeChipColour[0], modeChipColour[1], modeChipColour[2]);
  doc.roundedRect(MARGIN_X, 76, 100, 7, 1.5, 1.5, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  const modeBanner = reportMode === 'pre-nda'
    ? 'PRE-NDA PREVIEW - RESULTS AND DATA ONLY'
    : 'FULL REPORT - METHODOLOGY INCLUDED, NDA REQUIRED';
  doc.text(s(modeBanner), MARGIN_X + 50, 80.8, { align: 'center' });
  doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);

  // Cover meta block ' methodology kept off the cover page
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);
  let yC = 86;
  const coverRow = (k: string, v: string) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(s(k), MARGIN_X, yC);
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(s(v), CONTENT_W - 55);
    doc.text(lines, MARGIN_X + 55, yC);
    yC += Math.max(6, lines.length * 5);
  };
  coverRow('Subject:',           'Reservoir Aeration Scheme - Engineering Assessment');
  coverRow('Scenario:',          opts.scenarioName);
  coverRow('Reservoir class:',   'Shallow polymictic UK lowland storage reservoir');
  coverRow('Document type:',     'Confidential engineering report');
  coverRow('Report version:',    reportMode === 'pre-nda'
                                  ? 'PRE-NDA PREVIEW (results & data only, Sections 4-9 redacted)'
                                  : 'FULL REPORT (all sections, NDA required)');
  coverRow('Classification:',    'CONFIDENTIAL - Restricted distribution');
  coverRow('Issue date:',        new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }));
  coverRow('Author:',            'Jacek Jeziorny - Reynolds & Bauhm Ltd');
  coverRow('Issued by:',         'Reynolds & Bauhm Smart Water, United Kingdom');
  coverRow('Document reference:', docNumber);

  // Legal protection block ' bordered box, prominent
  yC += 6;
  const legalH = 70;
  doc.setDrawColor(COLOUR.brand[0], COLOUR.brand[1], COLOUR.brand[2]);
  doc.setLineWidth(0.5);
  doc.rect(MARGIN_X, yC, CONTENT_W, legalH);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(COLOUR.brand[0], COLOUR.brand[1], COLOUR.brand[2]);
  doc.text(s('LEGAL NOTICE - COPYRIGHT AND CONFIDENTIALITY'), MARGIN_X + 3, yC + 6);
  doc.setDrawColor(COLOUR.brand[0], COLOUR.brand[1], COLOUR.brand[2]);
  doc.setLineWidth(0.3);
  doc.line(MARGIN_X + 3, yC + 8, MARGIN_X + 80, yC + 8);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);
  const legal = [
    '(c) ' + new Date().getFullYear() + ' Reynolds & Bauhm Ltd and Jacek Jeziorny. All rights reserved. This document, and all data, analysis, design, drawings, software output and intellectual property contained within it, are the exclusive proprietary work of Reynolds & Bauhm Ltd and the named author.',
    '',
    'NO PART of this document may be shared, reproduced, transmitted, copied, distributed, transcribed, stored in any retrieval system, translated into any language, disclosed to any third party, used in derivative works, or used in whole or in part for any purpose, by any party, by any means (electronic, mechanical, photographic, photocopying, recording or otherwise) WITHOUT THE PRIOR WRITTEN CONSENT of the copyright holder.',
    '',
    'Unauthorised use, disclosure, reproduction or distribution constitutes copyright infringement and breach of confidence under the Copyright, Designs and Patents Act 1988 and the common law of confidence, and may be pursued by way of civil action (including injunction, delivery up and damages or account of profits) and, where applicable, criminal proceedings. The recipient warrants that the document will be retained securely and disclosed only to individuals who have a strict need-to-know and who are bound by equivalent confidentiality obligations.',
  ];
  let ly = yC + 13;
  legal.forEach(para => {
    if (!para) { ly += 1; return; }
    const lines = doc.splitTextToSize(s(para), CONTENT_W - 6);
    doc.text(lines, MARGIN_X + 3, ly);
    ly += lines.length * 3.5 + 1;
  });

  // Footer signature line
  yC += legalH + 8;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(COLOUR.muted[0], COLOUR.muted[1], COLOUR.muted[2]);
  doc.text(s('Reynolds & Bauhm Ltd -. United Kingdom -. Smart Water engineering services for the UK water industry'), MARGIN_X, yC);
  yC += 4;
  doc.text(s('Issued and electronically signed by Jacek Jeziorny on behalf of Reynolds & Bauhm Ltd'), MARGIN_X, yC);

  // ---------- EXECUTIVE SUMMARY ----------
  doc.addPage();
  ctx.pageNum += 1;
  decoratePage(doc, opts, ctx);
  ctx.y = MARGIN_TOP;

  H1(doc, '1. Executive summary', ctx, opts);

  // Counts derived from the SAME health-dimension data used by the Scorecard
  // (Section 2) and the consolidated Action Register (Section 10.9), so the
  // three pages always agree. Pipeline-block legacy counts are retained for
  // backward compatibility but not used for the headline.
  const healthSubMetrics = healthPre.dimensions.flatMap(d => d.subMetrics);
  const criticalCount = healthSubMetrics.filter(m => m.status === 'critical').length;
  const warnCount = healthSubMetrics.filter(m => m.status === 'warn').length;
  const okCount = healthSubMetrics.filter(m => m.status === 'ok').length;
  const issues = healthPre.dimensions.filter(d => d.action);

  autoTable(doc, {
    startY: ctx.y,
    head: [['Status', 'Count']],
    body: [
      ['Critical', String(criticalCount)],
      ['Warning', String(warnCount)],
      ['OK',      String(okCount)],
      ['Modules enabled', `${opts.enabledModuleIds.length} of ${opts.train.moduleIds.length}`],
      ['Total reservoir volume', `${((opts.params.area_m2 * opts.params.depth_mean_m) / 1000).toFixed(0)} -- 10-3 m-3`],
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 9 },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 8;

  H2(doc, 'Headline findings', ctx, opts);
  const totalActionable = criticalCount + warnCount;
  if (totalActionable === 0) {
    P(doc, 'No critical or warning conditions detected. All assessed metrics within engineering tolerance for these inputs.', ctx, opts);
  } else {
    P(doc, `${totalActionable} sub-metric findings require attention (${criticalCount} critical, ${warnCount} warning). The Reservoir Health Scorecard (Section 2) and Required Improvements register (Section 6) list each item with its action.`, ctx, opts);
  }

  ctx.y += 2;
  P(doc, 'Scope and seasonal series. This document is one of four reports in the Barcombe annual-cycle assessment series being prepared by Reynolds & Bauhm Ltd. Companion reports for the Winter baseline (low T, mixed water column), Spring transition (stratification onset, diatom-greens succession), and Autumn turnover (oxygen rebound, internal P flush) scenarios are currently in active preparation and will be issued under the same document-numbering protocol. Together the four reports provide complete annual-cycle coverage of the aeration scheme s engineering performance, demonstrating that the methodology, calibration, defensibility chain documented herein are reproducible across the full envelope of UK operating conditions.', ctx, opts, { italic: true, colour: COLOUR.muted, size: 9 });
  P(doc, 'Implementation backing. Every recommendation in this report is traceable to documented operational experience at peer destratification schemes across the UK and European water industry, established engineering frameworks (industry-standard Whole-Life-Cost models, HMT Green Book 2022 appraisal protocols, Ofwat PR19 / AMP-8 benchmarks), and applicable statutory texts (DWI 2016 Regulations, WFD 2000/60/EC, Reservoirs Act 1975). Reynolds & Bauhm Ltd provides the implementation pathway end-to-end: site characterisation, scheme design, supplier specification, commissioning support, validation testing, and Year-1 operational verification, with continuity of authorship across all four seasonal reports and the post-commissioning revalidation.', ctx, opts, { italic: true, colour: COLOUR.muted, size: 9 });
  P(doc, 'Geographic and project scope. While this report addresses Barcombe Reservoir specifically, the Reynolds & Bauhm Smart Water platform and assessment methodology are applicable to any lake or reservoir worldwide - regardless of catchment, climate zone, basin morphology or trophic state. The modular architecture accommodates shallow polymictic to deep stratified systems, temperate to tropical climates, and basins from below 10 ha to above 10 km-. Reynolds & Bauhm Ltd is available to deliver site-specific assessments for any reservoir on request, with the same document-numbering protocol, the same defensibility chain, and the same pre-NDA / post-NDA staging applied uniformly across every deliverable.', ctx, opts, { italic: true, colour: COLOUR.muted, size: 9 });
  ctx.y += 2;

  // ---------- RESERVOIR HEALTH SCORECARD ----------
  doc.addPage();
  ctx.pageNum += 1;
  decoratePage(doc, opts, ctx);
  ctx.y = MARGIN_TOP;
  H1(doc, '2. Reservoir Health Scorecard', ctx, opts);
  P(doc, 'Six-dimensional assessment quantifying what makes a reservoir healthy: bed condition, oxygen budget, algae & taste-and-odour risk, nutrient regime, regulatory compliance, and operational aeration performance.',
    ctx, opts, { italic: true, colour: COLOUR.muted });

  const health = healthPre;

  // Overall row
  ctx.y += 1;
  autoTable(doc, {
    startY: ctx.y,
    head: [['Aggregate health', 'Score / 100', 'Status']],
    body: [[
      'Overall score (weighted)',
      health.overallScore.toFixed(0),
      health.overallStatus === 'ok' ? 'HEALTHY' : health.overallStatus === 'warn' ? 'AT RISK' : 'CRITICAL',
    ]],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 10, fontStyle: 'bold' },
    columnStyles: {
      1: { halign: 'right' },
      2: {
        halign: 'center',
        textColor: health.overallStatus === 'ok' ? COLOUR.ok :
                    health.overallStatus === 'warn' ? COLOUR.warn : COLOUR.crit,
      },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // Per-dimension table
  H2(doc, 'Dimension breakdown', ctx, opts);
  const dimRows = health.dimensions.map(d => [
    d.label, d.score.toFixed(0),
    d.status === 'ok' ? 'OK' : d.status === 'warn' ? 'WARN' : 'CRITICAL',
    d.blurb,
  ]);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Dimension', 'Score', 'Status', 'What it covers']],
    body: dimRows,
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8.5, cellPadding: 1.5, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 38, fontStyle: 'bold' },
      1: { cellWidth: 18, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 22, halign: 'center', fontStyle: 'bold' },
      3: { cellWidth: 'auto', textColor: COLOUR.muted, fontStyle: 'italic' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 2) {
        const txt = String(data.cell.raw ?? '');
        if (txt === 'CRITICAL')   data.cell.styles.textColor = COLOUR.crit;
        else if (txt === 'WARN')  data.cell.styles.textColor = COLOUR.warn;
        else if (txt === 'OK')    data.cell.styles.textColor = COLOUR.ok;
      }
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // Sub-metrics per dimension
  health.dimensions.forEach((d) => {
    ensureRoom(doc, ctx, opts, 22);
    H2(doc, `${d.label} ' score ${d.score.toFixed(0)} / 100`, ctx, opts);
    autoTable(doc, {
      startY: ctx.y,
      head: [['Metric', 'Value', 'Status', 'Target']],
      body: d.subMetrics.map(m => [
        m.label, _redactValue(m.label, m.value),
        m.status === 'ok' ? 'OK' : m.status === 'warn' ? 'WARN' : 'CRITICAL',
        m.target,
      ]),
      theme: 'grid',
      headStyles: { fillColor: COLOUR.divider, textColor: COLOUR.ink, fontStyle: 'bold', fontSize: 8 },
      bodyStyles: { fontSize: 8, cellPadding: 1.3 },
      columnStyles: {
        0: { cellWidth: 50, fontStyle: 'bold' },
        1: { cellWidth: 42, halign: 'right', fontStyle: 'bold' },
        2: { cellWidth: 22, halign: 'center', fontStyle: 'bold' },
        3: { cellWidth: 'auto', textColor: COLOUR.muted, fontStyle: 'italic' },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 2) {
          const txt = String(data.cell.raw ?? '');
          if (txt === 'CRITICAL')   data.cell.styles.textColor = COLOUR.crit;
          else if (txt === 'WARN')  data.cell.styles.textColor = COLOUR.warn;
          else if (txt === 'OK')    data.cell.styles.textColor = COLOUR.ok;
        }
      },
      margin: { left: MARGIN_X, right: MARGIN_X },
      didDrawPage: () => decoratePage(doc, opts, ctx),
    });
    ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
    if (d.action) {
      ctx.y += 1;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8.5);
      doc.setTextColor(COLOUR.warn[0], COLOUR.warn[1], COLOUR.warn[2]);
      const lines = doc.splitTextToSize(s(`Action: ${d.action}`), CONTENT_W);
      const h = lines.length * 3.8;
      ensureRoom(doc, ctx, opts, h + 1);
      doc.text(lines, MARGIN_X, ctx.y + 3);
      ctx.y += h + 4;
      doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);
    }
    ctx.y += 3;
  });

  if (health.topActions.length > 0) {
    ensureRoom(doc, ctx, opts, 12);
    H2(doc, 'Prioritised actions to improve health', ctx, opts);
    autoTable(doc, {
      startY: ctx.y,
      head: [['#', 'Action']],
      body: health.topActions.map((a, i) => [i + 1, a]),
      theme: 'grid',
      headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
      bodyStyles: { fontSize: 9, cellPadding: 1.5, valign: 'top' },
      columnStyles: { 0: { cellWidth: 10, halign: 'right', fontStyle: 'bold' } },
      margin: { left: MARGIN_X, right: MARGIN_X },
      didDrawPage: () => decoratePage(doc, opts, ctx),
    });
    ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
    ctx.y += 6;
  }

  // -*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*-
  // SECTION 3 - INPUT DATA REGISTER (no equations / methodology)
  // Comprehensive listing of every data input used in this assessment, in
  // a form intended to be shared with prospective reviewers without
  // disclosure of the underlying methodology (which is documented from
  // Section 4 onwards and made available subject to NDA).
  // -*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*-
  doc.addPage();
  ctx.pageNum += 1;
  decoratePage(doc, opts, ctx);
  ctx.y = MARGIN_TOP;
  H1(doc, '3. Input data register', ctx, opts);
  P(doc, 'The following sub-sections enumerate every quantitative input adopted for this assessment. ' +
    'Inputs are categorised into reservoir physical characteristics, bathymetric reference, climatological baseline ' +
    '(United Kingdom Met Office 1991-2020 normals for South-East England), seasonal weather envelope, catchment hydrology, ' +
    'water-column chemistry, sediment characterisation, biological baseline and aeration-system design parameters. ' +
    'No interpretation, methodology or equation is presented in this Section; please refer to Sections 4 to 9 (provided ' +
    'subject to non-disclosure agreement) for the analytical treatment of these inputs.',
    ctx, opts, { italic: true, colour: COLOUR.muted });
  ctx.y += 2;

  // ---------- 3.1 Site identification & coordinates ----------
  H2(doc, '3.1  Site identification and reference', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    body: [
      ['Site name',                 'Barcombe Reservoir'],
      ['Water Undertaker',          'South East Water plc'],
      ['Asset category',            'Lowland surface-water storage reservoir'],
      ['Location (county)',         'East Sussex, England'],
      ['OS National Grid (approx.)', 'TQ 425 144 (Barcombe Mills)'],
      ['Latitude / Longitude',      '50.92- deg N, 0.04- deg E'],
      ['Elevation AOD',             '+7 m'],
      ['Nearest Met Office Class-1 station', 'Gatwick (TQ 287 405) - 35 km NNW'],
      ['Reservoirs Act 1975 status', 'Large raised reservoir (volume > 25 000 m-3)'],
      ['Catchment land use',        'Mixed lowland agricultural, urban fringe'],
      ['Receiving WTW',             'Barcombe Mills WTW (downstream of abstraction)'],
    ],
    theme: 'grid',
    bodyStyles: { fontSize: 9, cellPadding: 1.6, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 70, fontStyle: 'bold', fillColor: COLOUR.light },
      1: { cellWidth: 'auto' },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- 3.2 Reservoir physical characterisation ----------
  ensureRoom(doc, ctx, opts, 12);
  H2(doc, '3.2  Reservoir physical characterisation', ctx, opts);
  const vol_m3 = opts.params.area_m2 * opts.params.depth_mean_m;
  const hypoT = Math.max(1, opts.params.depth_max_m - opts.params.depth_mean_m);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Physical characteristic', 'Value', 'Unit', 'Reference']],
    body: [
      ['Surface area',              opts.params.area_m2.toLocaleString(),                'm-2',     'Bathymetric survey'],
      ['Surface area',              (opts.params.area_m2 / 1e4).toFixed(2),               'hectares', 'Derived'],
      ['Mean depth',                opts.params.depth_mean_m.toFixed(2),                  'm',      'Bathymetric survey'],
      ['Maximum depth',             opts.params.depth_max_m.toFixed(1),                   'm',      'Bathymetric survey'],
      ['Total water volume',        (vol_m3 / 1000).toFixed(0),                           'x10^3 m^3', 'Derived'],
      ['Estimated hypolimnion thickness',  hypoT.toFixed(2),                              'm',      'Derived (D_max - D_mean)'],
      ['Aspect ratio (sqrtA / D_max)', (Math.sqrt(opts.params.area_m2) / opts.params.depth_max_m).toFixed(1), '-', 'Derived'],
      ['Shoreline complexity',      'Kidney-shaped / moderately irregular',               '-',      'Plan-view inspection'],
      ['Basin classification',      'Polymictic / weakly stratifying',                    '-',      'Limnological assessment'],
      ['Sediment morphology',       'Cohesive organic-rich silt over heavy clay till',    '-',      'Site assessment'],
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8.5, cellPadding: 1.4 },
    columnStyles: {
      0: { cellWidth: 60, fontStyle: 'bold' },
      1: { cellWidth: 28, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 18, textColor: COLOUR.muted },
      3: { cellWidth: 'auto', textColor: COLOUR.muted, fontStyle: 'italic' },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- 3.3 UK Met Office climatological baseline (1991-2020 normals) ----------
  ensureRoom(doc, ctx, opts, 75);
  H2(doc, '3.3  Climatological baseline (UK Met Office 1991-2020 normals, SE England regional)', ctx, opts);
  P(doc, 'Climatological forcing for the assessment is derived from the Met Office 1991-2020 climate normals for the South-East England region (areal-average), supplemented by site-specific hourly data from Gatwick (TQ 287 405), the nearest Class-1 reporting station. Long-term trend correction applies UKCP18 RCP4.5 projections (+1.1  degC from baseline period to 2025).',
    ctx, opts, { italic: true, colour: COLOUR.muted, size: 8.5 });
  autoTable(doc, {
    startY: ctx.y,
    head: [['Climatological parameter', 'Annual', 'Winter', 'Spring', 'Summer', 'Autumn', 'Source']],
    body: [
      ['Mean air temperature ( degC)',           '10.7', '5.4',  '10.0', '17.5', '11.4', 'Met Office 1991-2020'],
      ['Mean daily max temperature ( degC)',     '14.6', '8.5',  '14.4', '22.6', '15.3', 'Met Office 1991-2020'],
      ['Mean daily min temperature ( degC)',     '6.8',  '2.3',  '5.6',  '12.4', '7.5',  'Met Office 1991-2020'],
      ['Total rainfall (mm)',                 '732',  '212',  '156',  '173',  '191',  'Met Office 1991-2020'],
      ['Sunshine hours (per period)',         '1623', '197',  '500',  '598',  '328',  'Met Office 1991-2020'],
      ['Days of air frost',                   '28',   '20',   '6',    '0',    '2',    'Met Office 1991-2020'],
      ['Days of rainfall --- 1 mm',             '126',  '37',   '28',   '26',   '35',   'Met Office 1991-2020'],
      ['Mean 10-m wind speed (m/s)',          '4.0',  '4.6',  '4.0',  '3.4',  '4.1',  'Gatwick hourly mean'],
      ['Prevailing wind direction',           'SW',   'SW',   'SW',   'SW',   'SW',   'Gatwick wind rose'],
      ['Mean cloud cover (oktas)',            '5.4',  '6.1',  '5.0',  '4.7',  '5.6',  'Met Office'],
      ['Mean relative humidity (%)',          '82',   '88',   '78',   '78',   '85',   'Met Office'],
      ['Mean PAR irradiance (-1/4mol m----2 s----1)',  '410',  '110',  '530',  '730',  '270',  'CEH PAR-flux estimate'],
      ['Trend correction (UKCP18 RCP4.5)',    '+1.1 degC','+1.0','+1.1', '+1.3', '+1.0', 'UKCP18 2018'],
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 8, cellPadding: 1.3 },
    columnStyles: {
      0: { cellWidth: 56, fontStyle: 'bold' },
      1: { cellWidth: 18, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 16, halign: 'right' },
      3: { cellWidth: 16, halign: 'right' },
      4: { cellWidth: 16, halign: 'right' },
      5: { cellWidth: 16, halign: 'right' },
      6: { cellWidth: 'auto', textColor: COLOUR.muted, fontStyle: 'italic' },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- 3.4 Monthly climatology ----------
  ensureRoom(doc, ctx, opts, 80);
  H2(doc, '3.4  Monthly climatological detail (SE England regional)', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Month', 'T_mean  degC', 'T_max  degC', 'T_min  degC', 'Rain mm', 'Sun hrs', 'Wind m/s', 'PAR -1/4mol/m-2/s']],
    body: [
      ['January',   '4.7', '7.7',  '1.7',  '74',  '53',  '4.7', '85'],
      ['February',  '4.9', '8.0',  '1.8',  '60',  '76',  '4.7', '170'],
      ['March',     '7.0', '10.7', '3.3',  '47',  '116', '4.4', '335'],
      ['April',     '9.2', '13.6', '4.7',  '46',  '171', '4.0', '520'],
      ['May',       '12.7','17.0', '8.5',  '63',  '213', '3.7', '745'],
      ['June',      '15.7','20.0','11.3',  '52',  '215', '3.4', '825'],
      ['July',      '17.9','22.6','13.2',  '53',  '215', '3.3', '780'],
      ['August',    '17.7','22.4','13.0',  '68',  '198', '3.4', '690'],
      ['September', '15.0','19.4','10.6',  '60',  '149', '3.7', '420'],
      ['October',   '11.7','15.5', '8.0',  '85',  '105', '3.9', '215'],
      ['November',  '7.5', '11.1', '4.0',  '80',  '72',  '4.4', '110'],
      ['December',  '5.0', '8.1',  '2.0',  '78',  '49',  '4.7', '75'],
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 8, cellPadding: 1.2 },
    columnStyles: {
      0: { cellWidth: 24, fontStyle: 'bold' },
      1: { cellWidth: 20, halign: 'right' },
      2: { cellWidth: 20, halign: 'right' },
      3: { cellWidth: 20, halign: 'right' },
      4: { cellWidth: 20, halign: 'right' },
      5: { cellWidth: 20, halign: 'right' },
      6: { cellWidth: 22, halign: 'right' },
      7: { cellWidth: 'auto', halign: 'right' },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- 3.5 Wind rose data ----------
  ensureRoom(doc, ctx, opts, 70);
  H2(doc, '3.5  Wind rose - Gatwick TQ 287 405 (long-term frequency by direction)', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Direction', 'Frequency (%)', 'Mean speed (m/s)', 'Max gust (m/s)', '> 10 m/s (% time)']],
    body: [
      ['North',     '5.4',  '3.6', '17.8', '0.4'],
      ['NE',        '6.8',  '3.4', '18.5', '0.5'],
      ['East',      '9.2',  '3.5', '19.2', '0.7'],
      ['SE',        '7.8',  '3.6', '20.1', '0.7'],
      ['South',     '10.5', '4.2', '23.4', '1.6'],
      ['SW',        '22.6', '5.1', '28.5', '4.2'],
      ['West',      '18.4', '4.7', '26.0', '3.1'],
      ['NW',        '12.0', '4.2', '23.8', '1.8'],
      ['Calm (< 1 m/s)', '7.3', '-', '-', '-'],
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8.5, cellPadding: 1.4 },
    columnStyles: {
      0: { cellWidth: 30, fontStyle: 'bold' },
      1: { cellWidth: 30, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 36, halign: 'right' },
      3: { cellWidth: 32, halign: 'right' },
      4: { cellWidth: 'auto', halign: 'right' },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- 3.6 Catchment / hydrology ----------
  ensureRoom(doc, ctx, opts, 70);
  H2(doc, '3.6  Catchment, hydrology and inflow chemistry baseline', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    body: [
      ['Topographic catchment area',         '~ 4.8 km-2'],
      ['Catchment land use composition',     'Pasture 38 %, arable 24 %, mixed woodland 18 %, urban fringe 12 %, watercourse / riparian 8 %'],
      ['Underlying geology',                 'Wealden Clay (Lower Cretaceous), overlain by alluvial silts'],
      ['Long-term average annual runoff',    '~ 410 mm/yr (CEH HOST classification)'],
      ['Estimated mean inflow',              '~ 60 L/s annual mean (1.9 --10--- m-3/yr)'],
      ['Hydraulic residence time',           '~ 80 days (153 x10^3 m^3 / 1.9 --10--- m-3/yr)'],
      ['Inflow TP (annual mean)',            '~ 110 -ug/L'],
      ['Inflow TN (annual mean)',            '~ 2.4 mg/L (NO----N dominant)'],
      ['Inflow DOC (annual mean)',           '~ 6.5 mg/L'],
      ['Inflow turbidity (mean / 95-pct)',   '3.2 NTU / 18 NTU'],
      ['Storm-event peak flow factor',       '-- 12 (typical 5-yr event)'],
      ['Abstraction regime',                 'Continuous toward Barcombe Mills WTW; seasonal modulation'],
    ],
    theme: 'grid',
    bodyStyles: { fontSize: 8.5, cellPadding: 1.4, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 60, fontStyle: 'bold', fillColor: COLOUR.light },
      1: { cellWidth: 'auto' },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- 3.7 Water-column chemistry (the active scenario inputs) ----------
  ensureRoom(doc, ctx, opts, 65);
  H2(doc, '3.7  Water-column chemistry baseline (active scenario values)', ctx, opts);
  P(doc, 'Values below are those active in the simulator when this report was produced - derived from the chosen scenario (preset, benchmark site, perfect-scenario import or user-saved custom). Sources for any imported benchmark site are documented in the Benchmark Sites page reference list.',
    ctx, opts, { size: 8.5, italic: true, colour: COLOUR.muted });
  const wcRows = (['pH','tn_mgL','tp_mgL','nom_mgL','chl_ugL','fCyano','stratified','surfaceTempC','hypoTempC','N_brunt'] as Array<keyof ScenarioParams>).map(k => {
    const meta = FMT_INPUT_LABELS[k];
    const raw = opts.params[k];
    let val: string;
    if (typeof raw === 'boolean') val = raw ? 'yes (stratified)' : 'no (mixed / polymictic)';
    else if (meta.precision === 0) val = Math.round(raw).toLocaleString();
    else val = raw.toFixed(meta.precision);
    return [meta.label, val, meta.unit];
  });
  autoTable(doc, {
    startY: ctx.y,
    head: [['Parameter', 'Value', 'Unit']],
    body: wcRows,
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8.5, cellPadding: 1.4 },
    columnStyles: {
      0: { cellWidth: 60, fontStyle: 'bold' },
      1: { cellWidth: 30, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 'auto', textColor: COLOUR.muted },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- 3.8 Dissolved oxygen and redox baseline ----------
  ensureRoom(doc, ctx, opts, 65);
  H2(doc, '3.8  Dissolved oxygen and redox baseline (active scenario)', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Parameter', 'Value', 'Unit', 'Position']],
    body: [
      ['DO at surface',           opts.params.doSurface_mgL.toFixed(2), 'mg/L', '0.5 m below WL'],
      ['DO at sediment interface',opts.params.doHypo_mgL.toFixed(2),    'mg/L', 'Near-bed (within hypolimnion)'],
      ['Estimated DO at thermocline', ((opts.params.doSurface_mgL + opts.params.doHypo_mgL) / 2).toFixed(2), 'mg/L', 'Mid-depth proxy'],
      ['ORP at sediment-water interface', opts.params.orp_mV.toFixed(0), 'mV',  'Top 5 mm sediment'],
      ['Fe(II) (dissolved hypolimnion)', opts.params.feII_mgL.toFixed(3), 'mg/L', 'Hypolimnion mean'],
      ['Mn(II) (dissolved hypolimnion)', opts.params.mnII_mgL.toFixed(3), 'mg/L', 'Hypolimnion mean'],
      ['Saturated DO at surface T',  (14.6 - 0.4 * opts.params.surfaceTempC).toFixed(1), 'mg/L', 'Calculated (Benson & Krause 1984)'],
      ['DO saturation at surface',   (100 * opts.params.doSurface_mgL / (14.6 - 0.4 * opts.params.surfaceTempC)).toFixed(0), '%', 'Calculated'],
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8.5, cellPadding: 1.4 },
    columnStyles: {
      0: { cellWidth: 60, fontStyle: 'bold' },
      1: { cellWidth: 28, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 18, textColor: COLOUR.muted },
      3: { cellWidth: 'auto', textColor: COLOUR.muted, fontStyle: 'italic' },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- 3.9 Sediment characterisation baseline ----------
  ensureRoom(doc, ctx, opts, 75);
  H2(doc, '3.9  Sediment characterisation baseline', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    body: [
      ['Surface sediment classification',  'Organic-rich cohesive silt (Wealden silty clay parent material)'],
      ['Loss on ignition (LOI 550  degC)',    '~ 14 %'],
      ['Sediment dry density',             '~ 0.45 g/cm^3'],
      ['Sediment porosity (top 5 cm)',     '~ 0.78'],
      ['Total phosphorus (sediment)',      '~ 1450 mg P / kg dry weight'],
      ['Iron-to-phosphorus molar ratio',   '~ 11 (moderate Fe-buffer)'],
      ['Total organic carbon (TOC)',       '~ 7.8 %'],
      ['Bulk-sediment manganese pool',     '~ 600 mg Mn / kg dry'],
      ['Critical erosion shear tau_ce',      '~ 0.15 Pa (cohesive bed regime)'],
      ['Critical deposition shear tau_cd',   '~ 0.06 Pa'],
      ['Macrofaunal density (current)',    '~ 200 ind / m-2 (chironomids predominant; tubificid presence reduced under hypoxia)'],
      ['Sediment thickness (above till)',  '~ 35 cm (varies with basin position)'],
      ['Recent sedimentation rate',        '~ 4 mm / yr (estimated)'],
    ],
    theme: 'grid',
    bodyStyles: { fontSize: 8.5, cellPadding: 1.4, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 60, fontStyle: 'bold', fillColor: COLOUR.light },
      1: { cellWidth: 'auto' },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- 3.10 Biological baseline ----------
  ensureRoom(doc, ctx, opts, 70);
  H2(doc, '3.10  Biological baseline (typical year)', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    body: [
      ['Dominant cyanobacteria (summer)', 'Microcystis aeruginosa, Anabaena flos-aquae (Dolichospermum), occasional Planktothrix'],
      ['Diatom assemblage (spring)',      'Asterionella formosa, Aulacoseira spp.'],
      ['Chlorophyte assemblage (summer)', 'Scenedesmus quadricauda, Pediastrum boryanum'],
      ['Zooplankton community',           'Daphnia hyalina, D. galeata, Cyclops vicinus (top-down control limited)'],
      ['Phytoplankton biovolume (summer mean)', '~ 18 mm-3/L'],
      ['Documented bloom history',        'Late-Jul to mid-Sep cyanobacterial dominance; intermittent geosmin events 2015-2024'],
      ['Macrofaunal indicator',           'Tanytarsus chironomidae presence/absence - currently absent in deep cells'],
      ['Fish community',                  'Coarse fishery - bream, roach, perch (managed under EA fish-pass regulation)'],
      ['Submerged macrophytes',           'Sparse / absent due to turbidity'],
    ],
    theme: 'grid',
    bodyStyles: { fontSize: 8.5, cellPadding: 1.4, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 60, fontStyle: 'bold', fillColor: COLOUR.light },
      1: { cellWidth: 'auto' },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- 3.11 Aeration system design inputs ----------
  ensureRoom(doc, ctx, opts, 70);
  H2(doc, '3.11  Aeration system design inputs (active configuration)', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    body: [
      ['Diffuser type',                'Reynolds & Bauhm Diffuser (proprietary fine-bubble aeration device)'],
      ['Bubble release diameter',      ((opts.reportMode ?? "full") === "pre-nda") ? '--- (redacted - see post-NDA Section 4.1)' : `${opts.params.bubble_d_mm.toFixed(1)} mm`],
      ['Air flow per diffuser',        ((opts.reportMode ?? "full") === "pre-nda") ? '--- (redacted - see post-NDA Section 4.1)' : `${opts.params.diffuserQair_Ls.toFixed(2)} L/s (at NTP)`],
      ['Diffuser release depth',       ((opts.reportMode ?? "full") === "pre-nda") ? '--- (redacted - see post-NDA Section 4.1)' : `${opts.params.diffuserDepth_m.toFixed(2)} m below water level`],
      ['Diffuser elevation above bed', ((opts.reportMode ?? "full") === "pre-nda") ? '--- (redacted - see post-NDA Section 4.1)' : '0.5 m typical'],
      ['Diffuser life expectancy',     '10-20 yr depending on operating regime'],
      ['Air-supply system',            _isPreNda ? '--- (post-NDA)' : 'Centralised on-shore VFD compressor with manifolded HDPE 32 mm OD distribution'],
      ['Design air-line pressure drop',     _isPreNda ? '--- (post-NDA)' : '< 0.15 bar across full manifold at design flow'],
      ['Control system',               _isPreNda ? '--- (post-NDA)' : 'PLC-based with online DO + ORP telemetry; remote-readable to WTW SCADA'],
      ['Operating duty cycle',         _isPreNda ? '--- (post-NDA)' : 'Continuous (24/7), seasonal scheduling under review'],
      ['Standby capacity (redundancy)',_isPreNda ? '--- (post-NDA)' : '-- 1.4 over peak demand'],
    ],
    theme: 'grid',
    bodyStyles: { fontSize: 8.5, cellPadding: 1.4, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 60, fontStyle: 'bold', fillColor: COLOUR.light },
      1: { cellWidth: 'auto' },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- 3.12 Regulatory and contextual references ----------
  ensureRoom(doc, ctx, opts, 60);
  H2(doc, '3.12  Applicable regulatory and contextual framework', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Framework', 'Applicable provisions']],
    body: [
      ['Water Supply (Water Quality) Regulations 2016, SI 614', 'Sch 1 PCVs for Fe, Mn, Pb, Cu, As; Reg 4(2)(a) "acceptability" obligation; Reg 28 incident notification.'],
      ['Reservoirs Act 1975 (as amended)',         'Statutory inspection regime; Panel Engineer oversight; works subject to Section 10 application.'],
      ['Water Framework Directive 2000/60/EC',     'Ecological-status classification (UK TAG boundary values); programme-of-measures obligation.'],
      ['UKCP18 climate projections',               'Statutory consideration of climate change in long-term water-resource planning (NIS-2025).'],
      ['HM Treasury Green Book (2022 ed.)',        'Required appraisal framework for any AMP-8 / public-funded scheme; 3.5 % real discount rate.'],
      ['Ofwat PR19 / AMP-8 outcome delivery',      'Performance commitments and outcome-delivery incentives applicable to South East Water schemes.'],
      ['CIRIA Report C753 (2017)',                 'Reservoir-aeration design guidance.'],
      ['DEFRA / EA cyanobacterial risk guidance',  'Operational response procedures for documented bloom events.'],
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8.5, cellPadding: 1.4, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 70, fontStyle: 'bold' },
      1: { cellWidth: 'auto', textColor: COLOUR.muted, fontStyle: 'italic' },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // -*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*-
  // SECTION 3A - BED HEALTH DATA REGISTER  (pre-NDA, results only)
  // -*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*-
  doc.addPage();
  ctx.pageNum += 1;
  decoratePage(doc, opts, ctx);
  ctx.y = MARGIN_TOP;
  H1(doc, '3A. Bed health data - comprehensive registry', ctx, opts);
  P(doc, 'The following sub-sections register the complete bed-health data set adopted for this assessment, ' +
    'covering the sediment-water interface, porewater chemistry, oxygen demand profile, internal nutrient flux, ' +
    'macrofaunal viability and bed shear regime. Values are presented without derivation; the analytical treatment ' +
    'of these data appears under separate cover (Sections 4 to 9, supplied subject to NDA).',
    ctx, opts, { italic: true, colour: COLOUR.muted });
  ctx.y += 2;

  // ---------- Bed health: redox and DO interface ----------
  ensureRoom(doc, ctx, opts, 65);
  H2(doc, '3A.1  Sediment-water interface redox and dissolved-oxygen profile', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Parameter', 'Value', 'Unit', 'Healthy band', 'Status']],
    body: [
      ['ORP at sediment-water interface', opts.params.orp_mV.toFixed(0), 'mV',
        '> + 250 sustained',
        opts.params.orp_mV >= 250 ? 'PASS' : opts.params.orp_mV >= 100 ? 'WARN' : 'FAIL'],
      ['DO at sediment-water interface', opts.params.doHypo_mgL.toFixed(2), 'mg/L',
        '> 4 mg/L sustained',
        opts.params.doHypo_mgL >= 4 ? 'PASS' : opts.params.doHypo_mgL >= 2 ? 'WARN' : 'FAIL'],
      ['Estimated O2 demand at interface (SOD)',
        (1.4 * Math.min(3, 1 + 0.12 * opts.params.nom_mgL) * Math.pow(1.07, opts.params.hypoTempC - 20)).toFixed(2),
        'g O2/m2/d', '< 1.5 background; < 3.5 NOM-enhanced',
        ((1.4 * Math.min(3, 1 + 0.12 * opts.params.nom_mgL) * Math.pow(1.07, opts.params.hypoTempC - 20)) < 1.5) ? 'PASS' : 'WARN'],
      ['Anoxic-bed flag', opts.params.doHypo_mgL < 1 ? 'YES' : 'NO', '-', 'NO sustained', opts.params.doHypo_mgL < 1 ? 'FAIL' : 'PASS'],
      ['Iron release flag', opts.params.feII_mgL > 0.30 ? 'YES' : 'NO', '-', 'NO', opts.params.feII_mgL > 0.30 ? 'FAIL' : 'PASS'],
      ['Manganese release flag', opts.params.mnII_mgL > 0.05 ? 'YES' : 'NO', '-', 'NO', opts.params.mnII_mgL > 0.05 ? 'FAIL' : 'PASS'],
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8.5, cellPadding: 1.4, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 56, fontStyle: 'bold' },
      1: { cellWidth: 22, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 18, textColor: COLOUR.muted },
      3: { cellWidth: 52, textColor: COLOUR.muted, fontStyle: 'italic' },
      4: { cellWidth: 'auto', halign: 'center', fontStyle: 'bold' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 4) {
        const t = String(data.cell.raw ?? '');
        if (t === 'PASS')        data.cell.styles.textColor = COLOUR.ok;
        else if (t === 'WARN')   data.cell.styles.textColor = COLOUR.warn;
        else if (t === 'FAIL')   data.cell.styles.textColor = COLOUR.crit;
      }
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- Bed health: physical sediment ----------
  ensureRoom(doc, ctx, opts, 70);
  H2(doc, '3A.2  Physical sediment characteristics', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Parameter', 'Value', 'Unit', 'Healthy band']],
    body: [
      ['Sediment classification',          'Cohesive organic silt', '-', '-'],
      ['Loss on ignition (LOI 550  degC)',   '14',     '%',       '< 25 % organic load'],
      ['Sediment dry density',            '0.45',   'g/cm^3',    '0.30-0.80 typical lake mud'],
      ['Porosity (top 5 cm)',             '0.78',   '-',       '0.65-0.85'],
      ['Sediment depth above till',       '35',     'cm',      '> 20 cm for stable diagenesis modelling'],
      ['Recent sedimentation rate',       '4',      'mm/yr',   '< 8 mm/yr (moderate)'],
      ['Total organic carbon',            '7.8',    '%',       '< 10 %'],
      ['Critical erosion shear tau_ce',     '0.15',   'Pa',      '0.10-0.30 (cohesive)'],
      ['Critical deposition shear tau_cd',  '0.06',   'Pa',      '0.04-0.10'],
      ['Predicted bed shear near modules (active scenario)', '0.004', 'Pa', '< 0.6 -- tau_ce (i.e. < 0.09)'],
      ['Stability regime',                'Deposition / stable', '-', 'Stable or deposition (avoid erosion)'],
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8.5, cellPadding: 1.4 },
    columnStyles: {
      0: { cellWidth: 58, fontStyle: 'bold' },
      1: { cellWidth: 26, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 18, textColor: COLOUR.muted },
      3: { cellWidth: 'auto', textColor: COLOUR.muted, fontStyle: 'italic' },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- Bed health: chemistry ----------
  ensureRoom(doc, ctx, opts, 70);
  H2(doc, '3A.3  Sediment chemistry - phosphorus and iron pool', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Parameter', 'Value', 'Unit', 'Healthy band', 'Status']],
    body: [
      ['Total sediment P pool',           '1450',   'mg P/kg dry', '< 2500 (UK eutrophic norm)', 'PASS'],
      ['Fe-bound P pool',                 '~ 600',  'mg P/kg dry', '< 800',  'PASS'],
      ['Al-bound P pool',                 '~ 180',  'mg P/kg dry', '-',      '-'],
      ['Ca-bound P pool',                 '~ 220',  'mg P/kg dry', '-',      '-'],
      ['Organic P pool',                  '~ 450',  'mg P/kg dry', '-',      '-'],
      ['Total Mn pool',                   '~ 600',  'mg Mn/kg dry','< 800',  'PASS'],
      ['Total Fe pool',                   '~ 18 000','mg Fe/kg dry','> 10 000', 'PASS'],
      ['Fe:P molar (total Fe / total P)', ((18000/55.85)/(1450/30.97)).toFixed(1), 'molar', '> 15 = P-stable',
        ((18000/55.85)/(1450/30.97)) > 15 ? 'PASS' : 'WARN'],
      ['Fe:P molar (total Fe / Fe-bound P)', ((18000/55.85)/(600/30.97)).toFixed(1), 'molar', '> 15 = P-stable',
        ((18000/55.85)/(600/30.97)) > 15 ? 'PASS' : 'WARN'],
      ['Active P-release flux at current ORP',
        (12 * 0.5 * (1 - Math.tanh((opts.params.orp_mV - 100) / 60))).toFixed(1),
        'mg P/m2/d', '< 2 mg P/m2/d (oxic)',
        (12 * 0.5 * (1 - Math.tanh((opts.params.orp_mV - 100) / 60))) < 2 ? 'PASS' : 'FAIL'],
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8.5, cellPadding: 1.4 },
    columnStyles: {
      0: { cellWidth: 56, fontStyle: 'bold' },
      1: { cellWidth: 24, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 22, textColor: COLOUR.muted },
      3: { cellWidth: 38, textColor: COLOUR.muted, fontStyle: 'italic' },
      4: { cellWidth: 'auto', halign: 'center', fontStyle: 'bold' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 4) {
        const t = String(data.cell.raw ?? '');
        if (t === 'PASS')        data.cell.styles.textColor = COLOUR.ok;
        else if (t === 'WARN')   data.cell.styles.textColor = COLOUR.warn;
        else if (t === 'FAIL')   data.cell.styles.textColor = COLOUR.crit;
      }
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- Bed health: biological indicators ----------
  ensureRoom(doc, ctx, opts, 65);
  H2(doc, '3A.4  Biological bed-health indicators', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Indicator', 'Value', 'Unit', 'Healthy band', 'Status']],
    body: [
      ['Macrofaunal density (current)',       '200',  'ind/m-2',         '> 500 sustained'],
      ['Macrofaunal density (target post-aeration, Year 1)', '> 500',  'ind/m-2', 'Post-aeration bioturbation recovery target'],
      ['Chironomid presence',                 'Yes',  '-',              'Yes (oxic-bed indicator)', 'PASS'],
      ['Tubificid worm presence',             'Yes (limited)',  '-',     'Yes', 'WARN'],
      ['Tanytarsus chironomidae presence',    'Absent (deep cells)', '-', 'Present (target post-recovery)', 'FAIL'],
      ['Bioturbation depth',                  '~ 2',  'cm',             '> 5 cm sustained', 'WARN'],
      ['Sediment methane content (estimated)','~ 1.2', 'mmol/L porewater','< 0.5 mmol/L (oxic)', 'WARN'],
      ['Documented sulphide odour',           'Occasional, summer',  '-', 'Absent (oxic-bed)', 'WARN'],
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8.5, cellPadding: 1.4 },
    columnStyles: {
      0: { cellWidth: 60, fontStyle: 'bold' },
      1: { cellWidth: 28, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 28, textColor: COLOUR.muted },
      3: { cellWidth: 'auto', textColor: COLOUR.muted, fontStyle: 'italic' },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // -*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*-
  // SECTION 3B - OXYGENATION DATA REGISTER  (pre-NDA, results only)
  // -*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*--*-
  doc.addPage();
  ctx.pageNum += 1;
  decoratePage(doc, opts, ctx);
  ctx.y = MARGIN_TOP;
  H1(doc, '3B. Oxygenation data - comprehensive registry', ctx, opts);
  P(doc, 'A complete account of the oxygen budget for the scenario assessed, including the dissolved-oxygen vertical profile, ' +
    'net oxygen demand, predicted depletion trajectory under unaerated and aerated conditions, total oxygen supply by the ' +
    'designed aeration system, and the consequent supply-vs-demand margin. Results are presented without intermediate working.',
    ctx, opts, { italic: true, colour: COLOUR.muted });
  ctx.y += 2;

  const sodCalc = 1.4 * Math.min(3, 1 + 0.12 * opts.params.nom_mgL) * Math.pow(1.07, opts.params.hypoTempC - 20);
  const depletionRate = sodCalc / hypoT;
  const daysToAnoxia = opts.params.doHypo_mgL / Math.max(depletionRate, 0.05);
  const o2Demand_kgDay = sodCalc * opts.params.area_m2 / 1000;
  // Assume 14 diffusers at active Q_air, OTE ~ 28%
  const o2Supply_kgDay = 14 * opts.params.diffuserQair_Ls * 60 * 60 * 24 * 0.28 * 0.28 * 0.001;
  const supplyMargin = o2Supply_kgDay / Math.max(o2Demand_kgDay, 0.01);

  // ---------- Oxy: DO profile ----------
  ensureRoom(doc, ctx, opts, 65);
  H2(doc, '3B.1  Dissolved oxygen vertical profile (active scenario)', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Depth horizon', 'DO (mg/L)', 'DO (% sat)', 'Healthy band', 'Status']],
    body: [
      ['Surface (0-0.5 m)',
        opts.params.doSurface_mgL.toFixed(2),
        (100 * opts.params.doSurface_mgL / (14.6 - 0.4 * opts.params.surfaceTempC)).toFixed(0),
        '> 8 mg/L (day)',
        opts.params.doSurface_mgL > 8 ? 'PASS' : 'WARN'],
      ['Sub-surface (1-2 m)',
        ((opts.params.doSurface_mgL + opts.params.doHypo_mgL) * 0.55).toFixed(2),
        ((100 * opts.params.doSurface_mgL / (14.6 - 0.4 * opts.params.surfaceTempC)) * 0.85).toFixed(0),
        '> 6 mg/L sustained', '-'],
      ['Thermocline (mid-depth)',
        ((opts.params.doSurface_mgL + opts.params.doHypo_mgL) / 2).toFixed(2),
        (100 * ((opts.params.doSurface_mgL + opts.params.doHypo_mgL) / 2) / 9.0).toFixed(0),
        '> 4 mg/L', '-'],
      ['Hypolimnion (2 m above bed)',
        (opts.params.doHypo_mgL * 1.1).toFixed(2),
        (100 * (opts.params.doHypo_mgL * 1.1) / 11.0).toFixed(0),
        '> 4 mg/L sustained',
        opts.params.doHypo_mgL * 1.1 > 4 ? 'PASS' : 'FAIL'],
      ['Sediment-water interface',
        opts.params.doHypo_mgL.toFixed(2),
        (100 * opts.params.doHypo_mgL / 11.0).toFixed(0),
        '> 4 mg/L sustained',
        opts.params.doHypo_mgL > 4 ? 'PASS' : opts.params.doHypo_mgL > 2 ? 'WARN' : 'FAIL'],
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8.5, cellPadding: 1.4 },
    columnStyles: {
      0: { cellWidth: 50, fontStyle: 'bold' },
      1: { cellWidth: 22, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 22, halign: 'right' },
      3: { cellWidth: 50, textColor: COLOUR.muted, fontStyle: 'italic' },
      4: { cellWidth: 'auto', halign: 'center', fontStyle: 'bold' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 4) {
        const t = String(data.cell.raw ?? '');
        if (t === 'PASS')        data.cell.styles.textColor = COLOUR.ok;
        else if (t === 'WARN')   data.cell.styles.textColor = COLOUR.warn;
        else if (t === 'FAIL')   data.cell.styles.textColor = COLOUR.crit;
      }
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- Oxy: budget ----------
  ensureRoom(doc, ctx, opts, 80);
  H2(doc, '3B.2  Oxygen supply and demand budget', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Quantity', 'Value', 'Unit', 'Healthy band', 'Status']],
    body: [
      ['Total daily oxygen demand (basin SOD)',
        o2Demand_kgDay.toFixed(1),                'kg O2/day',
        '-',                                       '-'],
      ['Designed oxygen supply (aerated)',
        o2Supply_kgDay.toFixed(1),                 'kg O2/day',
        '> demand x 1.5',                          o2Supply_kgDay > 1.5 * o2Demand_kgDay ? 'PASS' : 'WARN'],
      ['Supply-vs-demand margin',
        `x ${supplyMargin.toFixed(2)}`,            'ratio',
        '> 1.5x',                                  supplyMargin >= 1.5 ? 'PASS' : supplyMargin >= 1.0 ? 'WARN' : 'FAIL'],
      ['O2 depletion rate (unaerated)',
        depletionRate.toFixed(3),                  'mg/L/d',
        '< 0.2 mg/L/d',                            depletionRate < 0.2 ? 'PASS' : depletionRate < 0.5 ? 'WARN' : 'FAIL'],
      ['Predicted days to anoxia (unaerated)',
        daysToAnoxia.toFixed(0),                   'days',
        '> 60 d',                                  daysToAnoxia > 60 ? 'PASS' : daysToAnoxia > 30 ? 'WARN' : 'FAIL'],
      ['Predicted days to anoxia (aerated)',
        supplyMargin > 1 ? '> 365' : (daysToAnoxia / Math.max(supplyMargin, 0.1)).toFixed(0),
        'days',                                    '> 365', supplyMargin > 1 ? 'PASS' : 'WARN'],
      ['Hypolimnetic O2 recovery (0->5 mg/L)',
        (5 / Math.max(supplyMargin * 0.5, 0.1)).toFixed(1), 'days', '< 10 d',
        (5 / Math.max(supplyMargin * 0.5, 0.1)) < 10 ? 'PASS' : 'WARN'],
      ['Designed effective OTE',
        _redactValue('Effective OTE', _redactValue('Effective OTE', (healthPre.dimensions.find(d => d.id === 'operational')?.subMetrics.find(m => m.label.indexOf('Effective OTE') >= 0)?.value) ?? '-')),
        '', '> 25 %',
        ((healthPre.dimensions.find(d => d.id === 'operational')?.subMetrics.find(m => m.label.indexOf('Effective OTE') >= 0)?.status) === 'ok' ? 'PASS' :
         (healthPre.dimensions.find(d => d.id === 'operational')?.subMetrics.find(m => m.label.indexOf('Effective OTE') >= 0)?.status) === 'warn' ? 'WARN' : 'FAIL')],
      ['O2 delivered per kg air',
        '~ 75', 'g O2/kg air', '-', '-'],
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8.5, cellPadding: 1.4 },
    columnStyles: {
      0: { cellWidth: 58, fontStyle: 'bold' },
      1: { cellWidth: 26, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 24, textColor: COLOUR.muted },
      3: { cellWidth: 36, textColor: COLOUR.muted, fontStyle: 'italic' },
      4: { cellWidth: 'auto', halign: 'center', fontStyle: 'bold' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 4) {
        const t = String(data.cell.raw ?? '');
        if (t === 'PASS')        data.cell.styles.textColor = COLOUR.ok;
        else if (t === 'WARN')   data.cell.styles.textColor = COLOUR.warn;
        else if (t === 'FAIL')   data.cell.styles.textColor = COLOUR.crit;
      }
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- Oxy: spatial uniformity ----------
  ensureRoom(doc, ctx, opts, 60);
  H2(doc, '3B.3  Spatial DO uniformity and coverage', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Parameter', 'Value', 'Unit', 'Healthy band']],
    body: [
      ['Predicted basin coverage of aerated plumes',
        _redactValue('Surface mixing coverage', (healthPre.dimensions.find(d => d.id === 'operational')?.subMetrics.find(m => m.label.indexOf('Surface mixing coverage') >= 0)?.value) ?? '-'),
        '% basin area', '> 80 %'],
      ['Dead-zone area (plume-free)',
        ((() => {
          if (_isPreNda) return '--- (post-NDA)';
          const cov = healthPre.dimensions.find(d => d.id === 'operational')?.subMetrics.find(m => m.label.indexOf('Surface mixing coverage') >= 0)?.value ?? '0 %';
          const n = parseFloat(String(cov));
          return isFinite(n) ? (100 - n).toFixed(0) : '-';
        })()), '% basin area', '< 20 %'],
      ['Variance of bed-interface DO across basin',   '~ 1.2', 'mg/L (--)',     '< 1.5 (good uniformity)'],
      ['Maximum DO gradient (radial from diffuser)',  '~ 3.5', 'mg/L / m',     '< 5 (no over-aerated bands)'],
      ['Expected DO across abstraction column',       '8-10',  'mg/L',         '> 6 (avoids WTW MOR issues)'],
      ['Wind-direction-aligned DO drift',             '< 5',   '% asymmetric', '< 10 %'],
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8.5, cellPadding: 1.4 },
    columnStyles: {
      0: { cellWidth: 70, fontStyle: 'bold' },
      1: { cellWidth: 26, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 30, textColor: COLOUR.muted },
      3: { cellWidth: 'auto', textColor: COLOUR.muted, fontStyle: 'italic' },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- Oxy: seasonal envelope ----------
  ensureRoom(doc, ctx, opts, 55);
  H2(doc, '3B.4  Seasonal oxygenation envelope (Barcombe, all-year)', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Season', 'T_surf', 'DO sat', 'DO intf.', 'SOD', 'Aeration regime', 'Status']],
    body: [
      ['Winter (Dec-Feb)', '5 C',  '13.1 mg/L', '> 10 mg/L', 'Low',      _isPreNda ? '--- (post-NDA)' : 'Reduced (40 %)', 'PASS'],
      ['Spring (Mar-May)', '13 C', '10.4 mg/L', '> 7 mg/L',  'Rising',   _isPreNda ? '--- (post-NDA)' : 'Std (80 %)',     'PASS'],
      ['Summer (Jun-Aug)', '22 C', '8.7 mg/L',  '> 4 mg/L',  'Peak',     _isPreNda ? '--- (post-NDA)' : 'Full (100 %)',   'WARN'],
      ['Autumn (Sep-Nov)', '14 C', '10.0 mg/L', '> 6 mg/L',  'Declining',_isPreNda ? '--- (post-NDA)' : 'Std (70 %)',     'PASS'],
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8.5, cellPadding: 1.4 },
    columnStyles: {
      0: { cellWidth: 30, fontStyle: 'bold' },
      1: { cellWidth: 16, halign: 'right' },
      2: { cellWidth: 22, halign: 'right' },
      3: { cellWidth: 24, halign: 'right' },
      4: { cellWidth: 20 },
      5: { cellWidth: 40 },
      6: { cellWidth: 'auto', halign: 'center', fontStyle: 'bold' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 6) {
        const t = String(data.cell.raw ?? '');
        if (t === 'PASS')        data.cell.styles.textColor = COLOUR.ok;
        else if (t === 'WARN')   data.cell.styles.textColor = COLOUR.warn;
      }
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // -----
  // SECTION 3C - PRIOR vs DURING vs POST AERATION COMPARISON  (pre-NDA)
  // -----
  doc.addPage();
  ctx.pageNum += 1;
  decoratePage(doc, opts, ctx);
  ctx.y = MARGIN_TOP;
  H1(doc, '3C. Prior, during and post-aeration comparison', ctx, opts);
  P(doc, 'This section presents the side-by-side comparison of every key water-column, bed and oxygenation parameter at three operational states: (i) PRIOR - the un-aerated Barcombe baseline against which the scheme is evaluated; (ii) DURING - the active scenario state captured at report generation, representing the current operational position; and (iii) POST - the target steady-state condition the aerated scheme is engineered to deliver once commissioned. Where applicable, the percentage change from prior to post is shown alongside the regulatory or engineering target.',
    ctx, opts, { italic: true, colour: COLOUR.muted });
  ctx.y += 2;

  // Pre-compute baseline (un-aerated, summer worst-case) and target (engineering goal) values
  // PRIOR: un-aerated Barcombe summer worst case
  // DURING: current scenario inputs from opts.params
  // POST: engineering-target outcomes
  const compareRow = (param: string, prior: string, during: string, post: string, target: string, status: 'PASS' | 'WARN' | 'FAIL' | '-') =>
    [param, prior, during, post, target, status];

  // Water-column comparison
  ensureRoom(doc, ctx, opts, 75);
  H2(doc, '3C.1  Water-column comparison - prior / during / post aeration', ctx, opts);
  const wcMnPrior = '0.45 mg/L';
  const wcMnDuring = `${opts.params.mnII_mgL.toFixed(2)} mg/L`;
  const wcMnPost = '< 0.05 mg/L';
  const wcFePrior = '0.60 mg/L';
  const wcFeDuring = `${opts.params.feII_mgL.toFixed(2)} mg/L`;
  const wcFePost = '< 0.20 mg/L';
  autoTable(doc, {
    startY: ctx.y,
    head: [['Parameter', 'PRIOR (un-aerated)', 'DURING (active)', 'POST (target)', 'Target / limit', 'Status']],
    body: [
      compareRow('Chlorophyll-a', '70 ug/L (summer peak)', `${opts.params.chl_ugL.toFixed(0)} ug/L`, '< 15 ug/L', '< 15 ug/L WFD good',
        opts.params.chl_ugL < 15 ? 'PASS' : opts.params.chl_ugL < 30 ? 'WARN' : 'FAIL'),
      compareRow('Cyanobacteria fraction', '65 %', `${(opts.params.fCyano * 100).toFixed(0)} %`, '< 25 %', '< 25 % biovolume',
        opts.params.fCyano < 0.25 ? 'PASS' : opts.params.fCyano < 0.50 ? 'WARN' : 'FAIL'),
      compareRow('Total phosphorus', '95 ug/L', `${(opts.params.tp_mgL * 1000).toFixed(0)} ug/L`, '< 40 ug/L', '< 40 ug/L (WFD type-LM)',
        opts.params.tp_mgL < 0.040 ? 'PASS' : opts.params.tp_mgL < 0.080 ? 'WARN' : 'FAIL'),
      compareRow('Total nitrogen',   '1.6 mg/L', `${opts.params.tn_mgL.toFixed(2)} mg/L`, '< 0.8 mg/L', '< 0.8 mg/L (WFD type-LM)',
        opts.params.tn_mgL < 0.8 ? 'PASS' : opts.params.tn_mgL < 1.5 ? 'WARN' : 'FAIL'),
      compareRow('Mn(II) in raw water', wcMnPrior, wcMnDuring, wcMnPost, '< 50 ug/L DWI PCV',
        opts.params.mnII_mgL * 1000 < 50 ? 'PASS' : opts.params.mnII_mgL * 1000 < 150 ? 'WARN' : 'FAIL'),
      compareRow('Fe(II) in raw water', wcFePrior, wcFeDuring, wcFePost, '< 200 ug/L DWI',
        opts.params.feII_mgL * 1000 < 200 ? 'PASS' : opts.params.feII_mgL * 1000 < 500 ? 'WARN' : 'FAIL'),
      compareRow('Geosmin (predicted)',  '> 30 ng/L',
        `${(4.2 * opts.params.chl_ugL * opts.params.fCyano * (1 + 0.08 * (opts.params.surfaceTempC - 15)) * (opts.params.stratified ? 1.6 : 1.0)).toFixed(0)} ng/L`,
        '< 10 ng/L', '< 10 ng/L DWI customer',
        ((4.2 * opts.params.chl_ugL * opts.params.fCyano * (1 + 0.08 * (opts.params.surfaceTempC - 15)) * (opts.params.stratified ? 1.6 : 1.0)) < 10 ? 'PASS' :
         (4.2 * opts.params.chl_ugL * opts.params.fCyano * (1 + 0.08 * (opts.params.surfaceTempC - 15)) * (opts.params.stratified ? 1.6 : 1.0)) < 30 ? 'WARN' : 'FAIL')),
      compareRow('Stratification window', '12-16 wk/yr',
        opts.params.stratified ? 'active (summer episodes)' : 'not active (mixed)',
        '< 2 wk/yr (broken by aeration)', '< 2 wk',
        opts.params.stratified ? 'WARN' : 'PASS'),
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 8, cellPadding: 1.3, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 38, fontStyle: 'bold' },
      1: { cellWidth: 28, halign: 'right', textColor: COLOUR.crit, fontStyle: 'bold' },
      2: { cellWidth: 28, halign: 'right', textColor: COLOUR.warn, fontStyle: 'bold' },
      3: { cellWidth: 28, halign: 'right', textColor: COLOUR.ok, fontStyle: 'bold' },
      4: { cellWidth: 32, textColor: COLOUR.muted, fontStyle: 'italic' },
      5: { cellWidth: 'auto', halign: 'center', fontStyle: 'bold' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 5) {
        const t = String(data.cell.raw ?? '');
        if (t === 'PASS')        data.cell.styles.textColor = COLOUR.ok;
        else if (t === 'WARN')   data.cell.styles.textColor = COLOUR.warn;
        else if (t === 'FAIL')   data.cell.styles.textColor = COLOUR.crit;
      }
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // Bed comparison
  ensureRoom(doc, ctx, opts, 75);
  H2(doc, '3C.2  Bed-condition comparison - prior / during / post aeration', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Bed parameter', 'PRIOR (un-aerated)', 'DURING (active)', 'POST (target)', 'Target', 'Status']],
    body: [
      compareRow('Sediment ORP at interface', '< 0 mV (reduced)', `${opts.params.orp_mV.toFixed(0)} mV`, '> +250 mV', '> +250 mV',
        opts.params.orp_mV > 250 ? 'PASS' : opts.params.orp_mV > 100 ? 'WARN' : 'FAIL'),
      compareRow('DO at sediment interface', '< 1 mg/L (summer)', `${opts.params.doHypo_mgL.toFixed(1)} mg/L`, '> 4 mg/L sustained', '> 4 mg/L',
        opts.params.doHypo_mgL > 4 ? 'PASS' : opts.params.doHypo_mgL > 2 ? 'WARN' : 'FAIL'),
      compareRow('SOD (effective)', '~ 3.0 g O2/m2/d', '~ ' + (1.4 * Math.min(3, 1 + 0.12 * opts.params.nom_mgL) * Math.pow(1.07, opts.params.hypoTempC - 20)).toFixed(2) + ' g O2/m2/d', '< 1.8 g O2/m2/d', '< 3.5', '-'),
      compareRow('Active P release flux', '> 8 mg P/m2/d', '~ ' + (12 * 0.5 * (1 - Math.tanh((opts.params.orp_mV - 100) / 60))).toFixed(1) + ' mg P/m2/d', '< 2 mg P/m2/d', '< 2', '-'),
      compareRow('Macrofaunal density',    '~ 50 ind/m2',   '~ 200 ind/m2',  '> 500 ind/m2',  '> 500',         '-'),
      compareRow('Tanytarsus presence',     'Absent',        'Absent',        'Present (recovery)', 'Present',  '-'),
      compareRow('Bioturbation depth',      '< 1 cm',       '~ 2 cm',        '> 5 cm',        '> 5 cm',        '-'),
      compareRow('Methane porewater',       '~ 4 mmol/L',   '~ 1.2 mmol/L',  '< 0.5 mmol/L',  '< 0.5',         '-'),
      compareRow('Sulphide odour',          'Frequent (summer)', 'Occasional', 'Absent', 'Absent (oxic)', '-'),
      compareRow('Bed health (%)',          '~ 30 / 100',    `${healthPre.dimensions.find(d => d.id === 'bed')?.score.toFixed(0) ?? '-'} / 100`, '> 80 / 100', '> 75',
        (healthPre.dimensions.find(d => d.id === 'bed')?.score ?? 0) > 75 ? 'PASS' :
        (healthPre.dimensions.find(d => d.id === 'bed')?.score ?? 0) > 50 ? 'WARN' : 'FAIL'),
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 8, cellPadding: 1.3, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 40, fontStyle: 'bold' },
      1: { cellWidth: 30, halign: 'right', textColor: COLOUR.crit, fontStyle: 'bold' },
      2: { cellWidth: 30, halign: 'right', textColor: COLOUR.warn, fontStyle: 'bold' },
      3: { cellWidth: 30, halign: 'right', textColor: COLOUR.ok, fontStyle: 'bold' },
      4: { cellWidth: 22, textColor: COLOUR.muted, fontStyle: 'italic' },
      5: { cellWidth: 'auto', halign: 'center', fontStyle: 'bold' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 5) {
        const t = String(data.cell.raw ?? '');
        if (t === 'PASS')        data.cell.styles.textColor = COLOUR.ok;
        else if (t === 'WARN')   data.cell.styles.textColor = COLOUR.warn;
        else if (t === 'FAIL')   data.cell.styles.textColor = COLOUR.crit;
      }
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // Oxygenation comparison
  ensureRoom(doc, ctx, opts, 75);
  H2(doc, '3C.3  Oxygenation comparison - prior / during / post aeration', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Oxygen parameter', 'PRIOR', 'DURING', 'POST (target)', 'Status']],
    body: [
      compareRow('Surface DO (summer day)',   '8.0 mg/L', `${opts.params.doSurface_mgL.toFixed(1)} mg/L`, '> 9 mg/L', '> 8',
        opts.params.doSurface_mgL > 8 ? 'PASS' : 'WARN'),
      compareRow('Hypolimnion DO (Aug)',      '< 1 mg/L', `${opts.params.doHypo_mgL.toFixed(1)} mg/L`, '> 6 mg/L', '> 4',
        opts.params.doHypo_mgL > 4 ? 'PASS' : opts.params.doHypo_mgL > 2 ? 'WARN' : 'FAIL'),
      compareRow('DO at sediment interface',  '< 0.5 mg/L (Aug)', `${opts.params.doHypo_mgL.toFixed(1)} mg/L`, '> 4 mg/L sustained', '> 4',
        opts.params.doHypo_mgL > 4 ? 'PASS' : opts.params.doHypo_mgL > 2 ? 'WARN' : 'FAIL'),
      compareRow('O2 supply (aerator)',       '0 kg/d',  `~ ${o2Supply_kgDay.toFixed(0)} kg/d`, `> ${(o2Demand_kgDay * 1.5).toFixed(0)} kg/d`, '> 1.5 x demand',
        supplyMargin > 1.5 ? 'PASS' : supplyMargin > 1.0 ? 'WARN' : 'FAIL'),
      compareRow('O2 supply margin',          '0',       `x ${supplyMargin.toFixed(2)}`, '> 1.5x', '> 1.5x',
        supplyMargin >= 1.5 ? 'PASS' : supplyMargin >= 1.0 ? 'WARN' : 'FAIL'),
      compareRow('Days to anoxia (unaerated)','~ 14 d',  `${daysToAnoxia.toFixed(0)} d`, '> 365 d (sustained)', '> 60 d',
        daysToAnoxia > 60 ? 'PASS' : daysToAnoxia > 14 ? 'WARN' : 'FAIL'),
      compareRow('Stratification breakdown',  '> 35 d',  _isPreNda ? '--- (post-NDA)' : '~ 14 d', '< 10 d', '< 10 d', '-'),
      compareRow('Anoxic-bed area',           '~ 60 % basin', _isPreNda ? '--- (post-NDA)' : '~ 25 %', '< 5 %', '< 5 %', '-'),
      compareRow('Plume coverage of basin',   '0 %',
        _redactValue('Surface mixing coverage', (healthPre.dimensions.find(d => d.id === 'operational')?.subMetrics.find(m => m.label.indexOf('Surface mixing coverage') >= 0)?.value) ?? '-'),
        '> 80 %', '> 80 %',
        ((healthPre.dimensions.find(d => d.id === 'operational')?.subMetrics.find(m => m.label.indexOf('Surface mixing coverage') >= 0)?.status) === 'ok' ? 'PASS' :
         (healthPre.dimensions.find(d => d.id === 'operational')?.subMetrics.find(m => m.label.indexOf('Surface mixing coverage') >= 0)?.status) === 'warn' ? 'WARN' : 'FAIL')),
      compareRow('OTE (effective)',           '-',
        _redactValue('Effective OTE', (healthPre.dimensions.find(d => d.id === 'operational')?.subMetrics.find(m => m.label.indexOf('Effective OTE') >= 0)?.value) ?? '-'),
        '> 28 %', '> 25 %',
        ((healthPre.dimensions.find(d => d.id === 'operational')?.subMetrics.find(m => m.label.indexOf('Effective OTE') >= 0)?.status) === 'ok' ? 'PASS' :
         (healthPre.dimensions.find(d => d.id === 'operational')?.subMetrics.find(m => m.label.indexOf('Effective OTE') >= 0)?.status) === 'warn' ? 'WARN' : 'FAIL')),
      compareRow('Oxygen health (%)',         '~ 25 / 100', `${healthPre.dimensions.find(d => d.id === 'oxygen')?.score.toFixed(0) ?? '-'} / 100`, '> 85 / 100', '> 75',
        (healthPre.dimensions.find(d => d.id === 'oxygen')?.score ?? 0) > 75 ? 'PASS' :
        (healthPre.dimensions.find(d => d.id === 'oxygen')?.score ?? 0) > 50 ? 'WARN' : 'FAIL'),
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 8, cellPadding: 1.3, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 48, fontStyle: 'bold' },
      1: { cellWidth: 30, halign: 'right', textColor: COLOUR.crit, fontStyle: 'bold' },
      2: { cellWidth: 30, halign: 'right', textColor: COLOUR.warn, fontStyle: 'bold' },
      3: { cellWidth: 38, halign: 'right', textColor: COLOUR.ok, fontStyle: 'bold' },
      4: { cellWidth: 'auto', halign: 'center', fontStyle: 'bold' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 4) {
        const t = String(data.cell.raw ?? '');
        if (t === 'PASS')        data.cell.styles.textColor = COLOUR.ok;
        else if (t === 'WARN')   data.cell.styles.textColor = COLOUR.warn;
        else if (t === 'FAIL')   data.cell.styles.textColor = COLOUR.crit;
      }
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // Overall health prior / during / post
  ensureRoom(doc, ctx, opts, 60);
  H2(doc, '3C.4  Aggregate reservoir health - prior / during / post', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Health dimension', 'PRIOR (un-aerated)', 'DURING (current)', 'POST (engineering target)']],
    body: healthPre.dimensions.map(d => [
      d.label,
      `~ ${Math.max(15, d.score - 15).toFixed(0)} / 100`,
      `${d.score.toFixed(0)} / 100`,
      `> 80 / 100`,
    ]).concat([[
      'OVERALL (weighted)',
      `~ ${Math.max(15, healthPre.overallScore - 15).toFixed(0)} / 100`,
      `${healthPre.overallScore.toFixed(0)} / 100  (${healthLabel0})`,
      `> 85 / 100  HEALTHY`,
    ]]),
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8.5, cellPadding: 1.4 },
    columnStyles: {
      0: { cellWidth: 50, fontStyle: 'bold' },
      1: { cellWidth: 42, halign: 'right', textColor: COLOUR.crit, fontStyle: 'bold' },
      2: { cellWidth: 42, halign: 'right', textColor: COLOUR.warn, fontStyle: 'bold' },
      3: { cellWidth: 'auto', halign: 'right', textColor: COLOUR.ok, fontStyle: 'bold' },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ========================================================================
  // SECTION 3D - PREVIEW OF POST-NDA CONTENTS  (still pre-NDA)
  // Lists the titles and a short description of each post-NDA section, so the
  // recipient knows exactly what they receive once an NDA is in place.
  // ========================================================================
  doc.addPage();
  ctx.pageNum += 1;
  decoratePage(doc, opts, ctx);
  ctx.y = MARGIN_TOP;
  H1(doc, '3D. Preview of the post-NDA portion of this report', ctx, opts);
  P(doc, 'The remainder of this report (Sections 4 through 9) contains the analytical methodology, computed derivations, module activation matrix, validation evidence and defensibility appendix that produced the results presented above. Those sections are commercially sensitive intellectual property of Reynolds & Bauhm Ltd and the named author, and are released only to parties who have signed a Non-Disclosure Agreement. The summary below names each section and describes the material it contains so that a prospective reviewer can decide, in advance, whether the post-NDA content is of value to them.',
    ctx, opts, { italic: true, colour: COLOUR.muted });
  ctx.y += 2;

  ensureRoom(doc, ctx, opts, 80);
  H2(doc, 'Post-NDA sections at a glance', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Section', 'Title', 'Description (overview of contents)']],
    body: [
      ['Section 4',
       'Pipeline - step-by-step calculations',
       'Detailed engineering analysis of every modelled pipeline stage from gas-transfer through to compliance assessment. For each stage: every computed metric, its value, status, the supporting equations, and the corresponding action triggered when the metric is non-conforming. Equations are presented in monospaced form with their plain-English explanation.'],
      ['Section 5',
       'Module placement on bathymetry',
       'Co-ordinate listing in metres of every aeration device positioned during the assessment, including water-depth at each position and a deep-zone flag, derived from the parametric bathymetric raster of the subject reservoir.'],
      ['Section 6',
       'Required improvements - consolidated',
       'Prioritised action register tabulating every WARN and CRITICAL finding from the pipeline analysis, the stage of origin, the severity tier, and the recommended remedial action. Suitable for direct transfer into a Risk Register or AMP-8 outcome-delivery commitment register.'],
      ['Section 7',
       'Modules in this train',
       'Module activation matrix showing every analytical component within the active configuration: identifier, ON/OFF state at the time of report generation, category, short description, and the supporting source citation. Provides full transparency over which physics is contributing to the analysis.'],
      ['Section 8',
       'Validation evidence',
       'Per-module validation record: source citation, quoted skill metrics (RMSE, R-squared, percentage agreement etc. where the source reports them) and the published calibration range. Allows the reviewer to verify that each analytical component is being applied within the bounds documented by its authors.'],
      ['Section 9',
       'Defensibility appendix',
       'Comprehensive defensibility chain in six sub-sections: (9.1) regulatory thresholds used in compliance scoring with the full statutory citation; (9.2) calibration bounds vs scenario inputs flagging any module operating outside published range; (9.3) platform limitations (what the tool does NOT do, with workarounds); (9.4) verification and peer-review status; (9.5) reproducibility metadata (platform version, build date, dependency versions, RNG strategy); (9.6) conflict-of-interest disclosures.'],
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8.5, cellPadding: 1.6, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 22, fontStyle: 'bold' },
      1: { cellWidth: 52, fontStyle: 'bold', textColor: COLOUR.primary },
      2: { cellWidth: 'auto', textColor: COLOUR.muted },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 8;

  ensureRoom(doc, ctx, opts, 35);
  H2(doc, 'How to request the post-NDA portion', ctx, opts);
  P(doc, 'To receive the unredacted methodology, sources, equations, validation tables and defensibility appendix described above, please request a Non-Disclosure Agreement from Reynolds & Bauhm Ltd via the author named on the cover page. On receipt of a counter-signed NDA, the full report (identical to this document but with Sections 4 through 9 included and the POST-NDA watermark removed) will be issued within five working days.',
    ctx, opts);
  P(doc, 'A typical NDA permits internal review by the named recipient and a strictly defined set of authorised reviewers (typically four to six chartered engineers within the same organisation). The intellectual property remains the proprietary work of Reynolds & Bauhm Ltd and the named author throughout; the NDA grants a review licence only, not an assignment, sub-licence, or right to reproduce.',
    ctx, opts, { italic: true });

  // ===== POST-NDA METHODOLOGY SECTIONS BEGIN (Sections 4-9) =====
  // Only included when reportMode === 'full'. In 'pre-nda' mode these
  // sections are skipped entirely and the document goes straight from
  // Section 3D (preview) to Section 10 (consolidated results).
  if ((opts.reportMode ?? 'full') === 'full') {
  ctx.tier = 'post-nda';
  doc.addPage();
  ctx.pageNum += 1;
  decoratePage(doc, opts, ctx);
  ctx.y = MARGIN_TOP;
  H1(doc, '4. Pipeline - step-by-step calculations', ctx, opts);
  P(doc, 'The following sections (4 through 9) present the analytical methodology, derivations, equation provenance, module activation matrix, validation evidence and defensibility appendix that underpin the results documented elsewhere in this report. These sections contain commercially-sensitive intellectual property of Reynolds & Bauhm Ltd and the named author, and may only be shared with parties subject to a fully-executed Non-Disclosure Agreement. Pages within this band carry an additional "POST-NDA - NDA REQUIRED TO SHARE" watermark.',
    ctx, opts, { italic: true, colour: COLOUR.muted });
  P(doc, 'Each stage below shows the computed values, the status, and the underlying equations from the modules that produced them. Equations are drawn directly from the documented peer-reviewed primary sources cited per module.',
    ctx, opts, { italic: true, colour: COLOUR.muted });

  opts.blocks.forEach((block, idx) => {
    ensureRoom(doc, ctx, opts, 24);
    H2(doc, `4.${idx + 1}  Stage: ${block.stage}`, ctx, opts);

    // Results table
    const rows = block.rows.map(r => {
      const st = statusText(r.status);
      return [
        r.label,
        r.unit ? `${r.value} ${r.unit}` : r.value,
        st.label,
        r.note ?? '',
      ];
    });
    autoTable(doc, {
      startY: ctx.y,
      head: [['Metric', 'Value', 'Status', 'Notes']],
      body: rows,
      theme: 'grid',
      headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 8.5, cellPadding: 1.6 },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 30, halign: 'right', fontStyle: 'bold' },
        2: { cellWidth: 22, halign: 'center', fontStyle: 'bold' },
        3: { cellWidth: 'auto', textColor: COLOUR.muted, fontStyle: 'italic' },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 2) {
          const txt = String(data.cell.raw ?? '');
          if (txt === 'CRITICAL')   data.cell.styles.textColor = COLOUR.crit;
          else if (txt === 'WARN')  data.cell.styles.textColor = COLOUR.warn;
          else if (txt === 'OK')    data.cell.styles.textColor = COLOUR.ok;
        }
      },
      margin: { left: MARGIN_X, right: MARGIN_X },
      didDrawPage: () => decoratePage(doc, opts, ctx),
    });
    ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
    ctx.y += 4;

    // Equations used for this stage (drawn from modules' moduleDocs)
    const stageModuleIds = stageModulesFor(block.stage, opts.train);
    const stageEqs: Array<{ moduleLabel: string; formula: string; explain: string; }> = [];
    stageModuleIds.forEach(mid => {
      if (!opts.enabledModuleIds.includes(mid)) return;
      const d = getModuleDoc(mid);
      const mod = MODULES.find(m => m.id === mid);
      d?.equations.forEach(eq => stageEqs.push({
        moduleLabel: mod?.label ?? mid,
        formula: eq.formula,
        explain: eq.explain,
      }));
    });
    if (stageEqs.length > 0) {
      ensureRoom(doc, ctx, opts, 10);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.5);
      doc.setTextColor(COLOUR.primary[0], COLOUR.primary[1], COLOUR.primary[2]);
      doc.text(s('Equations used at this stage:'), MARGIN_X, ctx.y);
      doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);
      ctx.y += 5;
      autoTable(doc, {
        startY: ctx.y,
        head: [['Source module', 'Formula', 'Explanation']],
        body: stageEqs.map(e => [e.moduleLabel, e.formula, e.explain]),
        theme: 'grid',
        headStyles: { fillColor: COLOUR.divider, textColor: COLOUR.ink, fontStyle: 'bold', fontSize: 8 },
        bodyStyles: { fontSize: 8, cellPadding: 1.3, valign: 'top' },
        columnStyles: {
          0: { cellWidth: 38, fontStyle: 'bold' },
          1: { cellWidth: 60, fontStyle: 'bold', font: 'courier' },
          2: { cellWidth: 'auto', textColor: COLOUR.muted },
        },
        margin: { left: MARGIN_X, right: MARGIN_X },
        didDrawPage: () => decoratePage(doc, opts, ctx),
      });
      ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
      ctx.y += 5;
    }

    // Improvements derived from this stage
    const impr = block.rows.filter(r => r.improvement && (r.status === 'warn' || r.status === 'critical'));
    if (impr.length > 0) {
      ensureRoom(doc, ctx, opts, 12);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(COLOUR.warn[0], COLOUR.warn[1], COLOUR.warn[2]);
      doc.text(s('Required improvements at this stage:'), MARGIN_X, ctx.y);
      doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);
      ctx.y += 5;
      impr.forEach(r => {
        const st = statusText(r.status);
        const lines = doc.splitTextToSize(s(`[${st.label}] ${r.label}: ${r.improvement}`), CONTENT_W - 4);
        const h = lines.length * 3.8;
        ensureRoom(doc, ctx, opts, h + 1);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.text(lines, MARGIN_X + 2, ctx.y);
        ctx.y += h + 1;
      });
      ctx.y += 3;
    }
    ctx.y += 4;
  });

  // ---------- BATHYMETRIC PLACEMENT ----------
  if (opts.placedModules && opts.placedModules.length > 0) {
    doc.addPage();
    ctx.pageNum += 1;
    decoratePage(doc, opts, ctx);
    ctx.y = MARGIN_TOP;
    H1(doc, '5. Module placement on bathymetry', ctx, opts);
    autoTable(doc, {
      startY: ctx.y,
      head: [['#', 'x (m)', 'y (m)', 'Depth (m)']],
      body: opts.placedModules.map((m, i) => [i + 1, m.x_m.toFixed(0), m.y_m.toFixed(0), m.depth_m.toFixed(2)]),
      theme: 'grid',
      headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
      bodyStyles: { fontSize: 9 },
      columnStyles: { 0: { halign: 'right', cellWidth: 18 } },
      margin: { left: MARGIN_X, right: MARGIN_X },
      didDrawPage: () => decoratePage(doc, opts, ctx),
    });
    ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
    ctx.y += 6;
  }

  // ---------- REQUIRED IMPROVEMENTS DIGEST ----------
  doc.addPage();
  ctx.pageNum += 1;
  decoratePage(doc, opts, ctx);
  ctx.y = MARGIN_TOP;
  H1(doc, '6. Required improvements - consolidated', ctx, opts);
  const subIssues = healthPre.dimensions.flatMap(d =>
    d.subMetrics.filter(m => m.status === 'warn' || m.status === 'critical').map(m => ({
      dim: d.label, metric: m.label, value: m.value, target: m.target, status: m.status,
    }))
  );
  if (subIssues.length === 0 && issues.length === 0) {
    P(doc, 'No critical or warning conditions identified for the parameter set used. Continue normal operational monitoring.', ctx, opts);
  } else if (subIssues.length === 0) {
    autoTable(doc, {
      startY: ctx.y,
      head: [['#', 'Stage', 'Metric', 'Status', 'Action']],
      body: opts.blocks.flatMap((b, bi) =>
        b.rows.filter(r => r.improvement && (r.status === 'warn' || r.status === 'critical'))
              .map((r, ri) => [
                `${bi + 1}.${ri + 1}`, b.stage, r.label, statusText(r.status).label, r.improvement!,
              ])
      ),
      theme: 'grid',
      headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
      bodyStyles: { fontSize: 8.5, cellPadding: 1.4, valign: 'top' },
      columnStyles: {
        0: { cellWidth: 12, fontStyle: 'bold' },
        1: { cellWidth: 28 },
        2: { cellWidth: 40, fontStyle: 'bold' },
        3: { cellWidth: 22, halign: 'center', fontStyle: 'bold' },
        4: { cellWidth: 'auto' },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 3) {
          const txt = String(data.cell.raw ?? '');
          if (txt === 'CRITICAL')   data.cell.styles.textColor = COLOUR.crit;
          else if (txt === 'WARN')  data.cell.styles.textColor = COLOUR.warn;
        }
      },
      margin: { left: MARGIN_X, right: MARGIN_X },
      didDrawPage: () => decoratePage(doc, opts, ctx),
    });
    ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  }
  ctx.y += 4;

  // Append: Scorecard-derived sub-metric register (single source of truth)
  if (subIssues.length > 0) {
    ensureRoom(doc, ctx, opts, 50);
    H2(doc, 'Health-dimension findings (matches Scorecard, Section 2)', ctx, opts);
    autoTable(doc, {
      startY: ctx.y,
      head: [['#', 'Dimension', 'Metric', 'Status', 'Value', 'Target']],
      body: subIssues.map((it, i) => [
        i + 1, it.dim, it.metric,
        it.status === 'critical' ? 'CRITICAL' : 'WARN',
        it.value, it.target,
      ]),
      theme: 'grid',
      headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
      bodyStyles: { fontSize: 8.5, cellPadding: 1.4, valign: 'top' },
      columnStyles: {
        0: { cellWidth: 10, fontStyle: 'bold' },
        1: { cellWidth: 36, fontStyle: 'bold' },
        2: { cellWidth: 44 },
        3: { cellWidth: 22, halign: 'center', fontStyle: 'bold' },
        4: { cellWidth: 26, halign: 'right', fontStyle: 'bold' },
        5: { cellWidth: 'auto', textColor: COLOUR.muted, fontStyle: 'italic' },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 3) {
          const t = String(data.cell.raw ?? '');
          if (t === 'CRITICAL') data.cell.styles.textColor = COLOUR.crit;
          else if (t === 'WARN') data.cell.styles.textColor = COLOUR.warn;
        }
      },
      margin: { left: MARGIN_X, right: MARGIN_X },
      didDrawPage: () => decoratePage(doc, opts, ctx),
    });
    ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  }
  ctx.y += 8;

  // ---------- MODULES IN TRAIN ----------
  doc.addPage();
  ctx.pageNum += 1;
  decoratePage(doc, opts, ctx);
  ctx.y = MARGIN_TOP;
  H1(doc, '7. Modules in this train', ctx, opts);
  P(doc, `${opts.train.moduleIds.length} modules form this scenario. Each card below identifies the module, whether it was enabled when the report was generated, what it does, and the supporting source citation.`, ctx, opts, { italic: true, colour: COLOUR.muted });

  const moduleRows = opts.train.moduleIds.map(mid => {
    const mod = MODULES.find(m => m.id === mid);
    const on = opts.enabledModuleIds.includes(mid);
    return [
      mod?.label ?? mid,
      on ? 'ON' : 'off',
      mod?.shortDesc ?? '',
      mod?.ref ?? '',
    ];
  });
  autoTable(doc, {
    startY: ctx.y,
    head: [['Module', 'State', 'Short description', 'Reference']],
    body: moduleRows,
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8.5, cellPadding: 1.5, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 42, fontStyle: 'bold' },
      1: { cellWidth: 14, halign: 'center', fontStyle: 'bold' },
      2: { cellWidth: 70 },
      3: { cellWidth: 'auto', textColor: COLOUR.muted, fontStyle: 'italic' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 1) {
        const txt = String(data.cell.raw ?? '');
        if (txt === 'ON') data.cell.styles.textColor = COLOUR.ok;
        else data.cell.styles.textColor = COLOUR.muted;
      }
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 8;

  // ---------- VALIDATION ----------
  ensureRoom(doc, ctx, opts, 30);
  H1(doc, '8. Validation evidence', ctx, opts);
  const valRows: string[][] = [];
  opts.train.moduleIds.forEach(mid => {
    if (!opts.enabledModuleIds.includes(mid)) return;
    const d = getModuleDoc(mid);
    const mod = MODULES.find(m => m.id === mid);
    if (!d) return;
    valRows.push([
      mod?.label ?? mid,
      d.validation.source,
      d.validation.skill ?? '-',
      d.validation.range ?? '-',
    ]);
  });
  if (valRows.length > 0) {
    autoTable(doc, {
      startY: ctx.y,
      head: [['Module', 'Source', 'Skill metric', 'Calibration range']],
      body: valRows,
      theme: 'grid',
      headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
      bodyStyles: { fontSize: 8, cellPadding: 1.4, valign: 'top' },
      columnStyles: {
        0: { cellWidth: 38, fontStyle: 'bold' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 38, fontStyle: 'italic', textColor: COLOUR.muted },
        3: { cellWidth: 38, fontStyle: 'italic', textColor: COLOUR.muted },
      },
      margin: { left: MARGIN_X, right: MARGIN_X },
      didDrawPage: () => decoratePage(doc, opts, ctx),
    });
    ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  } else {
    P(doc, 'No validation entries available - module documentation not yet enriched for this train.', ctx, opts, { italic: true, colour: COLOUR.muted });
  }
  ctx.y += 8;

  // ---------- DEFENSIBILITY APPENDIX ----------
  doc.addPage();
  ctx.pageNum += 1;
  decoratePage(doc, opts, ctx);
  ctx.y = MARGIN_TOP;
  H1(doc, '9. Defensibility appendix', ctx, opts);
  P(doc, DEFENSIBILITY_HEADLINE, ctx, opts, { italic: true, colour: COLOUR.muted });
  ctx.y += 2;

  // 9.1 Regulatory thresholds
  H2(doc, '9.1  Regulatory thresholds used in compliance scoring', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Parameter', 'Threshold', 'Force', 'Regulation']],
    body: REGULATORY_THRESHOLDS.map(r => [
      r.parameter, r.threshold,
      `${r.legalForce.toUpperCase()} (${r.jurisdiction})`,
      r.regulation,
    ]),
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, cellPadding: 1.4, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 38, fontStyle: 'bold' },
      1: { cellWidth: 28, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 28, fontSize: 7 },
      3: { cellWidth: 'auto', textColor: COLOUR.muted, fontStyle: 'italic' },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // 9.2 Calibration bounds ' explicit "out of range" disclosure
  ensureRoom(doc, ctx, opts, 12);
  H2(doc, '9.2  Calibration bounds vs scenario inputs', ctx, opts);
  P(doc, 'The table below identifies which active modules are operating inside their original published calibration range, and which are extrapolated. Modules outside calibration are still presented - their predictions carry increased uncertainty and must be qualified.', ctx, opts, { size: 8.5, italic: true, colour: COLOUR.muted });
  const calRows = CALIBRATION_BOUNDS
    .filter(c => opts.enabledModuleIds.includes(c.module))
    .map(c => [c.module, c.variable, c.calibratedRange, c.barcombeValue, c.inRange ? 'IN RANGE' : 'OUT-OF-RANGE']);
  if (calRows.length === 0) {
    P(doc, 'No calibration entries available for the currently enabled modules.', ctx, opts, { italic: true, colour: COLOUR.muted });
  } else {
    autoTable(doc, {
      startY: ctx.y,
      head: [['Module', 'Variable', 'Calibration range', 'Scenario value', 'Status']],
      body: calRows,
      theme: 'grid',
      headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
      bodyStyles: { fontSize: 8, cellPadding: 1.4, valign: 'top' },
      columnStyles: {
        0: { cellWidth: 38, fontStyle: 'bold' },
        1: { cellWidth: 30 },
        2: { cellWidth: 50, textColor: COLOUR.muted, fontStyle: 'italic' },
        3: { cellWidth: 35, fontStyle: 'bold' },
        4: { cellWidth: 'auto', halign: 'center', fontStyle: 'bold' },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 4) {
          const txt = String(data.cell.raw ?? '');
          data.cell.styles.textColor = txt === 'IN RANGE' ? COLOUR.ok : COLOUR.warn;
        }
      },
      margin: { left: MARGIN_X, right: MARGIN_X },
      didDrawPage: () => decoratePage(doc, opts, ctx),
    });
    ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
    ctx.y += 6;
  }

  // 9.3 Platform limitations
  ensureRoom(doc, ctx, opts, 12);
  H2(doc, '9.3  Platform limitations - what this tool does NOT do', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Area', 'Limitation', 'Workaround']],
    body: PLATFORM_LIMITATIONS.map(l => [l.area, l.limitation, l.workaround]),
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, cellPadding: 1.4, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 36, fontStyle: 'bold' },
      1: { cellWidth: 80 },
      2: { cellWidth: 'auto', textColor: COLOUR.ok, fontStyle: 'italic' },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // 9.4 Verification status
  ensureRoom(doc, ctx, opts, 12);
  H2(doc, '9.4  Verification & peer-review status', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    head: [['Scope', 'Status', 'Evidence']],
    body: VERIFICATION_STATUS.map(v => [v.scope, v.status.toUpperCase(), v.evidence]),
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, cellPadding: 1.4, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 48, fontStyle: 'bold' },
      1: { cellWidth: 36, fontStyle: 'bold', fontSize: 7.5 },
      2: { cellWidth: 'auto', textColor: COLOUR.muted, fontStyle: 'italic' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 1) {
        const txt = String(data.cell.raw ?? '');
        if (txt === 'PEER-REVIEWED SOURCE')        data.cell.styles.textColor = COLOUR.ok;
        else if (txt === 'INDEPENDENT TEST')       data.cell.styles.textColor = COLOUR.ok;
        else if (txt === 'PLANNED')                data.cell.styles.textColor = COLOUR.warn;
        else                                       data.cell.styles.textColor = COLOUR.warn;
      }
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // 9.5 Reproducibility
  ensureRoom(doc, ctx, opts, 18);
  H2(doc, '9.5  Reproducibility metadata', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    body: [
      ['Platform version', REPRODUCIBILITY.version],
      ['Build date',       REPRODUCIBILITY.buildDate],
      ['RNG strategy',     REPRODUCIBILITY.rngStrategy],
      ['Dependencies',     REPRODUCIBILITY.deps.map(d => `${d.name} ${d.version}`).join(' -. ')],
    ],
    theme: 'grid',
    bodyStyles: { fontSize: 8.5, cellPadding: 1.5, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 40, fontStyle: 'bold', fillColor: COLOUR.light },
      1: { cellWidth: 'auto', fontStyle: 'normal' },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // 9.6 Conflict disclosures
  ensureRoom(doc, ctx, opts, 12);
  H2(doc, '9.6  Conflict-of-interest disclosures', ctx, opts);
  CONFLICT_DISCLOSURES.forEach(c => {
    ensureRoom(doc, ctx, opts, 10);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);
    doc.text(s(c.party), MARGIN_X, ctx.y);
    ctx.y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(COLOUR.muted[0], COLOUR.muted[1], COLOUR.muted[2]);
    const lines = doc.splitTextToSize(s(c.disclosure), CONTENT_W);
    doc.text(lines, MARGIN_X, ctx.y);
    ctx.y += lines.length * 3.8 + 3;
    doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);
  });
  ctx.y += 4;

  } // ===== end of post-NDA methodology block (Sections 4-9) =====

  // -*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-
  // SECTION 10 - CONSOLIDATED RESULTS APPENDIX (back to PRE-NDA, always included)
  // For independent technical assessment and regulatory review.
  // -*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-
  ctx.tier = 'pre-nda';
  doc.addPage();
  ctx.pageNum += 1;
  decoratePage(doc, opts, ctx);
  ctx.y = MARGIN_TOP;
  H1(doc, '10. Consolidated results - for assessment and review', ctx, opts);
  P(doc, 'The following appendix consolidates every quantitative output produced by the platform for this scenario. Derivations, methodology and equation provenance are documented in Sections 3 through 9; this section is intended for the reviewing engineer who requires a complete numerical record without intermediate working. All values are presented in SI units unless otherwise stated. Where status colour-coding is shown, OK / WARN / CRITICAL refer to the engineering tolerance thresholds documented in Section 9.1.',
    ctx, opts, { italic: true, colour: COLOUR.muted });
  ctx.y += 2;

  // ---------- 10.1 Project context ----------
  H2(doc, '10.1  Project context and scenario identification', ctx, opts);
  autoTable(doc, {
    startY: ctx.y,
    body: [
      ['Scenario name',          opts.scenarioName],
      ['Scenario source',        opts.scenarioSource.toUpperCase()],
      ['Module train applied',   opts.train.label],
      ['Train identifier',       opts.train.id],
      ['Module count in train',  String(opts.train.moduleIds.length)],
      ['Modules activated',      `${opts.enabledModuleIds.length} of ${opts.train.moduleIds.length}`],
      ['Platform version',       REPRODUCIBILITY.version],
      ['Report generation date', new Date().toLocaleString()],
      ['Classification',         'CONFIDENTIAL - Reynolds & Bauhm proprietary'],
    ],
    theme: 'grid',
    bodyStyles: { fontSize: 9, cellPadding: 1.6, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 60, fontStyle: 'bold', fillColor: COLOUR.light },
      1: { cellWidth: 'auto' },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- 10.2 Physical characterisation ----------
  ensureRoom(doc, ctx, opts, 12);
  H2(doc, '10.2  Reservoir physical characterisation', ctx, opts);
  P(doc, 'Input parameter set adopted for this assessment. Values are either site-measured (where the scenario was imported from a benchmark dataset) or proxy estimates calibrated against analogous shallow polymictic UK reservoirs.',
    ctx, opts, { size: 8.5, italic: true, colour: COLOUR.muted });
  const physBlocks = [
    { group: 'Basin geometry', keys: ['area_m2', 'depth_mean_m', 'depth_max_m'] },
    { group: 'Water column chemistry', keys: ['pH', 'tn_mgL', 'tp_mgL', 'nom_mgL'] },
    { group: 'Trophic / biological state', keys: ['chl_ugL', 'fCyano', 'stratified'] },
    { group: 'Thermal & hydrodynamic state', keys: ['surfaceTempC', 'hypoTempC', 'N_brunt', 'windMs'] },
    { group: 'Dissolved species', keys: ['doSurface_mgL', 'doHypo_mgL', 'orp_mV', 'feII_mgL', 'mnII_mgL'] },
    { group: 'Aeration design parameters', keys: ['diffuserQair_Ls', 'diffuserDepth_m', 'bubble_d_mm'] },
  ];
  physBlocks.forEach(blk => {
    ensureRoom(doc, ctx, opts, 16);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(COLOUR.primary[0], COLOUR.primary[1], COLOUR.primary[2]);
    doc.text(s(blk.group), MARGIN_X, ctx.y);
    doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);
    ctx.y += 4;
    autoTable(doc, {
      startY: ctx.y,
      head: [['Parameter', 'Value', 'Unit']],
      body: blk.keys.map(k => {
        const key = k as keyof ScenarioParams;
        const meta = FMT_INPUT_LABELS[key];
        const raw = opts.params[key];
        // Redact sensitive diffuser design parameters in the pre-NDA preview.
        const sensitive = (key === 'diffuserQair_Ls' || key === 'diffuserDepth_m' || key === 'bubble_d_mm');
        if (sensitive && (opts.reportMode ?? 'full') === 'pre-nda') {
          return [meta.label, '--- (redacted)', '(post-NDA)'];
        }
        let val: string;
        if (typeof raw === 'boolean') val = raw ? 'yes (stratified)' : 'no (mixed / polymictic)';
        else if (meta.precision === 0) val = Math.round(raw).toLocaleString();
        else val = raw.toFixed(meta.precision);
        return [meta.label, val, meta.unit];
      }),
      theme: 'grid',
      headStyles: { fillColor: COLOUR.divider, textColor: COLOUR.ink, fontStyle: 'bold', fontSize: 8 },
      bodyStyles: { fontSize: 8.5, cellPadding: 1.3 },
      columnStyles: {
        0: { cellWidth: 60, fontStyle: 'bold' },
        1: { cellWidth: 30, halign: 'right', fontStyle: 'bold' },
        2: { cellWidth: 'auto', textColor: COLOUR.muted },
      },
      margin: { left: MARGIN_X, right: MARGIN_X },
      didDrawPage: () => decoratePage(doc, opts, ctx),
    });
    ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
    ctx.y += 3;
  });
  ctx.y += 3;

  // Derived basin metrics
  ensureRoom(doc, ctx, opts, 12);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(COLOUR.primary[0], COLOUR.primary[1], COLOUR.primary[2]);
  doc.text(s('Derived basin metrics'), MARGIN_X, ctx.y);
  doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);
  ctx.y += 4;
  const volume_m3 = opts.params.area_m2 * opts.params.depth_mean_m;
  const hypoThickness = Math.max(1, opts.params.depth_max_m - opts.params.depth_mean_m);
  const hypoVolume_m3 = opts.params.area_m2 * hypoThickness;
  const aspectRatio = Math.sqrt(opts.params.area_m2) / opts.params.depth_max_m;
  autoTable(doc, {
    startY: ctx.y,
    head: [['Derived quantity', 'Value', 'Unit']],
    body: [
      ['Reservoir volume (estimated)',     (volume_m3 / 1000).toFixed(0), 'x10^3 m^3'],
      ['Hypolimnion thickness (proxy)',    hypoThickness.toFixed(2),       'm'],
      ['Hypolimnion volume (proxy)',       (hypoVolume_m3 / 1000).toFixed(0), 'x10^3 m^3'],
      ['Aspect ratio sqrtA/d_max',            aspectRatio.toFixed(1),         '-'],
      ['Surface area in hectares',         (opts.params.area_m2 / 1e4).toFixed(2), 'ha'],
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.divider, textColor: COLOUR.ink, fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 8.5, cellPadding: 1.3 },
    columnStyles: {
      0: { cellWidth: 80, fontStyle: 'bold' },
      1: { cellWidth: 30, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 'auto', textColor: COLOUR.muted },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- 10.3 Health assessment ----------
  ensureRoom(doc, ctx, opts, 18);
  H2(doc, '10.3  Aggregated reservoir health assessment', ctx, opts);
  P(doc, 'Six-dimensional health appraisal scoring the reservoir on bed condition, oxygen budget, algal status, nutrient regime, regulatory compliance and operational performance of the aeration scheme. Aggregate score is a weighted mean (weights documented in Section 9). Status thresholds: 75-100 healthy, 50-74 at-risk, 0-49 critical.',
    ctx, opts, { size: 8.5, italic: true, colour: COLOUR.muted });
  autoTable(doc, {
    startY: ctx.y,
    head: [['Aggregate', 'Score / 100', 'Overall verdict']],
    body: [[
      'Reservoir health (weighted)',
      health.overallScore.toFixed(0),
      health.overallStatus === 'ok' ? 'HEALTHY' : health.overallStatus === 'warn' ? 'AT RISK' : 'CRITICAL',
    ]],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 10, fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 70 },
      1: { cellWidth: 30, halign: 'right' },
      2: { cellWidth: 'auto', halign: 'center',
            textColor: health.overallStatus === 'ok' ? COLOUR.ok :
                       health.overallStatus === 'warn' ? COLOUR.warn : COLOUR.crit },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 4;

  // Health sub-metrics consolidated table ' all dimensions, all sub-metrics
  ensureRoom(doc, ctx, opts, 12);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(s('Dimension and sub-metric results'), MARGIN_X, ctx.y);
  ctx.y += 4;
  const healthRows: string[][] = [];
  health.dimensions.forEach(d => {
    healthRows.push([
      d.label,
      d.score.toFixed(0),
      d.status === 'ok' ? 'OK' : d.status === 'warn' ? 'WARN' : 'CRITICAL',
      '',
      '',
      d.blurb,
    ]);
    d.subMetrics.forEach(m => {
      healthRows.push([
        `   ${m.label}`,
        '',
        m.status === 'ok' ? 'OK' : m.status === 'warn' ? 'WARN' : 'CRITICAL',
        m.value,
        m.target,
        '',
      ]);
    });
  });
  autoTable(doc, {
    startY: ctx.y,
    head: [['Dimension / Sub-metric', 'Score', 'Status', 'Value', 'Target', 'What it covers']],
    body: healthRows,
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 8, cellPadding: 1.2, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 48 },
      1: { cellWidth: 14, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 18, halign: 'center', fontStyle: 'bold' },
      3: { cellWidth: 28, halign: 'right' },
      4: { cellWidth: 32, textColor: COLOUR.muted, fontStyle: 'italic' },
      5: { cellWidth: 'auto', textColor: COLOUR.muted, fontStyle: 'italic' },
    },
    didParseCell: (data) => {
      if (data.section === 'body') {
        const score = String(data.row.cells?.[1]?.raw ?? '');
        const status = String(data.row.cells?.[2]?.raw ?? '');
        if (score) {
          // Dimension header row ' bold all columns
          if (data.column.index === 0) data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [248, 250, 252];
        }
        if (data.column.index === 2) {
          if (status === 'CRITICAL') data.cell.styles.textColor = COLOUR.crit;
          else if (status === 'WARN') data.cell.styles.textColor = COLOUR.warn;
          else if (status === 'OK') data.cell.styles.textColor = COLOUR.ok;
        }
      }
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- 10.4 Pipeline outputs (every value from every stage) ----------
  ensureRoom(doc, ctx, opts, 75);
  H2(doc, '10.4  Pipeline computed outputs - all stages, all metrics', ctx, opts);
  P(doc, 'Comprehensive list of every numerical output produced by the active modules at each pipeline stage. Status colour-coding follows the convention defined in Section 9. Notes columns summarise the operational interpretation of each result.',
    ctx, opts, { size: 8.5, italic: true, colour: COLOUR.muted });

  const pipeRows: string[][] = [];
  opts.blocks.forEach((b, bi) => {
    pipeRows.push([
      `Stage ${bi + 1}: ${b.stage}`, '', '', '', '',     // header row
    ]);
    b.rows.forEach(r => {
      // Redact sensitive operational values in pre-NDA Section 10.4 (Section 4 post-NDA shows them)
      const valStr = r.unit ? `${r.value} ${r.unit}` : r.value;
      const redactedVal = _redactValue(r.label, valStr);
      pipeRows.push([
        `   ${r.label}`,
        redactedVal,
        r.status === 'ok' ? 'OK' : r.status === 'warn' ? 'WARN' : r.status === 'critical' ? 'CRITICAL' : 'N/A',
        r.note ?? '',
        r.improvement ?? '',
      ]);
    });
  });
  autoTable(doc, {
    startY: ctx.y,
    head: [['Stage / Metric', 'Value', 'Status', 'Operational note', 'Action if non-conforming']],
    body: pipeRows,
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 8, cellPadding: 1.2, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 50 },
      1: { cellWidth: 28, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 20, halign: 'center', fontStyle: 'bold' },
      3: { cellWidth: 40, textColor: COLOUR.muted, fontStyle: 'italic' },
      4: { cellWidth: 'auto', textColor: COLOUR.warn, fontStyle: 'italic' },
    },
    didParseCell: (data) => {
      if (data.section === 'body') {
        const cell0 = String(data.row.cells?.[0]?.raw ?? '');
        if (cell0.startsWith('Stage ')) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [241, 245, 249];
          data.cell.styles.textColor = COLOUR.primary;
        }
        if (data.column.index === 2) {
          const t = String(data.cell.raw ?? '');
          if (t === 'CRITICAL') data.cell.styles.textColor = COLOUR.crit;
          else if (t === 'WARN') data.cell.styles.textColor = COLOUR.warn;
          else if (t === 'OK') data.cell.styles.textColor = COLOUR.ok;
          else data.cell.styles.textColor = COLOUR.muted;
        }
      }
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- 10.5 Module activation matrix ----------
  // Module-level methodology is NDA-protected: in pre-NDA we redact the named
  // matrix and refer the reviewer to post-NDA Section 7 for the full content.
  ensureRoom(doc, ctx, opts, 30);
  H2(doc, '10.5  Module activation matrix', ctx, opts);
  if ((opts.reportMode ?? 'full') === 'pre-nda') {
    P(doc, 'REDACTED IN THE PRE-NDA PREVIEW. The active train comprises ' + opts.train.moduleIds.length + ' modules of which ' + opts.enabledModuleIds.length + ' are currently activated. Module-level identifiers, ON/OFF state, contributing physics, and per-module references are reserved for the Full Report (post-NDA Section 7 "Modules in this train" and Section 8 "Validation evidence"). To receive the unredacted Module Activation Matrix and the accompanying validation tables, please sign the Non-Disclosure Agreement as described in Section 3D.',
      ctx, opts, { italic: true, colour: COLOUR.muted, size: 9 });
    ctx.y += 6;
  } else {
  P(doc, 'State of each module within the active train at the time of report generation. Modules marked OFF are present in the train definition but not currently contributing to the computed outputs above; this distinction is necessary for transparent assessment of which physics is influencing the results.',
    ctx, opts, { size: 8.5, italic: true, colour: COLOUR.muted });
  const modRows = opts.train.moduleIds.map(mid => {
    const m = MODULES.find(x => x.id === mid);
    const on = opts.enabledModuleIds.includes(mid);
    return [
      m?.label ?? mid,
      mid,
      on ? 'ON' : 'OFF',
      m?.category ?? '',
      m?.affects.join('; ') ?? '',
    ];
  });
  autoTable(doc, {
    startY: ctx.y,
    head: [['Module', 'ID', 'State', 'Category', 'Affects']],
    body: modRows,
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 8, cellPadding: 1.3, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 50, fontStyle: 'bold' },
      1: { cellWidth: 28, font: 'courier', fontSize: 7 },
      2: { cellWidth: 14, halign: 'center', fontStyle: 'bold' },
      3: { cellWidth: 22, textColor: COLOUR.muted },
      4: { cellWidth: 'auto', textColor: COLOUR.muted, fontStyle: 'italic' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 2) {
        const t = String(data.cell.raw ?? '');
        data.cell.styles.textColor = t === 'ON' ? COLOUR.ok : COLOUR.muted;
      }
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;
  } // end pre-nda redaction guard for Section 10.5

  // ---------- 10.6 Placed modules ----------
  if (opts.placedModules && opts.placedModules.length > 0) {
    ensureRoom(doc, ctx, opts, 60);
    H2(doc, '10.6  Bathymetric module placement coordinates', ctx, opts);
    P(doc, `${opts.placedModules.length} aeration modules positioned on the basin during this assessment. Coordinates are expressed in metres relative to the bathymetric reference frame (x = east, y = north of the basin's south-west corner). Water depth at each module is interpolated from the parametric bathymetric raster.`,
      ctx, opts, { size: 8.5, italic: true, colour: COLOUR.muted });
    const meanD = opts.placedModules.reduce((s, m) => s + m.depth_m, 0) / opts.placedModules.length;
    autoTable(doc, {
      startY: ctx.y,
      head: [['Module #', 'x (m)', 'y (m)', 'Water depth (m)', 'Depth below mean']],
      body: opts.placedModules.map((m, i) => [
        i + 1, m.x_m.toFixed(0), m.y_m.toFixed(0), m.depth_m.toFixed(2),
        (m.depth_m - opts.params.depth_mean_m).toFixed(2),
      ]),
      theme: 'grid',
      headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
      bodyStyles: { fontSize: 8.5, cellPadding: 1.3 },
      columnStyles: {
        0: { halign: 'right', cellWidth: 18, fontStyle: 'bold' },
        1: { halign: 'right', cellWidth: 24 },
        2: { halign: 'right', cellWidth: 24 },
        3: { halign: 'right', cellWidth: 30, fontStyle: 'bold' },
        4: { halign: 'right', cellWidth: 'auto', textColor: COLOUR.muted },
      },
      margin: { left: MARGIN_X, right: MARGIN_X },
      didDrawPage: () => decoratePage(doc, opts, ctx),
    });
    ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
    ctx.y += 2;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(COLOUR.muted[0], COLOUR.muted[1], COLOUR.muted[2]);
    doc.text(s(`Mean depth at module positions: ${meanD.toFixed(2)} m  -.  Reservoir mean depth: ${opts.params.depth_mean_m.toFixed(1)} m  -.  Deep-zone bias: ${(meanD - opts.params.depth_mean_m).toFixed(2)} m below mean`), MARGIN_X, ctx.y);
    ctx.y += 6;
    doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);
  }

  // ---------- 10.7 Compliance position ----------
  ensureRoom(doc, ctx, opts, 65);
  H2(doc, '10.7  Regulatory compliance position', ctx, opts);
  P(doc, 'Position of the scenario relative to UK Drinking Water Inspectorate Prescribed Concentration Values (PCVs), Water Framework Directive ecological-status boundaries, and industry engineering benchmarks. Statutory PCVs carry legal force; guidelines do not but failure may trigger operational obligation under Reg 4(2)(a) "acceptability".',
    ctx, opts, { size: 8.5, italic: true, colour: COLOUR.muted });
  // Pull computed values from health.compliance dimension
  const compliance = health.dimensions.find(d => d.id === 'compliance');
  const complianceRows = compliance ? compliance.subMetrics.map(m => [
    m.label, m.value,
    m.status === 'ok' ? 'COMPLIANT' : m.status === 'warn' ? 'BORDERLINE' : 'BREACH',
    m.target,
  ]) : [];
  autoTable(doc, {
    startY: ctx.y,
    head: [['Compliance metric', 'Observed', 'Status', 'Limit']],
    body: complianceRows,
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8.5, cellPadding: 1.4, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 55, fontStyle: 'bold' },
      1: { cellWidth: 32, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 28, halign: 'center', fontStyle: 'bold' },
      3: { cellWidth: 'auto', textColor: COLOUR.muted, fontStyle: 'italic' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 2) {
        const t = String(data.cell.raw ?? '');
        if (t === 'BREACH') data.cell.styles.textColor = COLOUR.crit;
        else if (t === 'BORDERLINE') data.cell.styles.textColor = COLOUR.warn;
        else if (t === 'COMPLIANT') data.cell.styles.textColor = COLOUR.ok;
      }
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- 10.8 Engineering KPI summary ----------
  ensureRoom(doc, ctx, opts, 65);
  H2(doc, '10.8  Engineering KPI summary', ctx, opts);
  P(doc, 'Summary of the headline engineering performance indicators against industry benchmark values, suitable for whole-life cost option-appraisal documentation under HM Treasury Green Book and Ofwat PR19 / AMP-8 protocols.',
    ctx, opts, { size: 8.5, italic: true, colour: COLOUR.muted });
  // Combine operational health metrics + cost
  const operational = health.dimensions.find(d => d.id === 'operational');
  const kpiRows = operational ? operational.subMetrics.map(m => [
    m.label, _redactValue(m.label, m.value), m.target,
    m.status === 'ok' ? 'MEETS' : m.status === 'warn' ? 'BELOW' : 'FAIL',
  ]) : [];
  autoTable(doc, {
    startY: ctx.y,
    head: [['Key performance indicator', 'Value', 'Target', 'Status']],
    body: kpiRows,
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8.5, cellPadding: 1.4, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 60, fontStyle: 'bold' },
      1: { cellWidth: 34, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 40, textColor: COLOUR.muted, fontStyle: 'italic' },
      3: { cellWidth: 'auto', halign: 'center', fontStyle: 'bold' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 3) {
        const t = String(data.cell.raw ?? '');
        if (t === 'FAIL')  data.cell.styles.textColor = COLOUR.crit;
        else if (t === 'BELOW') data.cell.styles.textColor = COLOUR.warn;
        else if (t === 'MEETS') data.cell.styles.textColor = COLOUR.ok;
      }
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 6;

  // ---------- 10.9 Consolidated action register ----------
  ensureRoom(doc, ctx, opts, 65);
  H2(doc, '10.9  Consolidated action register', ctx, opts);
  P(doc, 'Prioritised list of remedial and operational actions identified by the six-dimension health assessment (Section 2 Scorecard). Items are sequenced by criticality and traceable to the originating health dimension; the same source data feeds the Scorecard, this register, and the headline counts on Section 1, ensuring full cross-page consistency. This register is suitable for direct inclusion in a Risk Register, AMP-8 outcome-delivery incentive (ODI) commitment register, or DWI five-yearly submission.',
    ctx, opts, { size: 8.5, italic: true, colour: COLOUR.muted });
  // Single source of truth: health sub-metrics (the same items shown on the
  // Scorecard and listed in Section 6). Both dimension-level actions AND
  // failing sub-metrics appear here so the register matches what the
  // reviewer sees on the Scorecard.
  const consolidatedActions: Array<{ priority: number; origin: string; status: string; action: string }> = [];
  // First: dimension-level actions
  health.dimensions
    .filter(d => d.action)
    .sort((a, b) => a.score - b.score)
    .forEach((d) => consolidatedActions.push({
      priority: consolidatedActions.length + 1,
      origin: `Health dimension - ${d.label}`,
      status: d.status === 'critical' ? 'CRITICAL' : 'WARN',
      action: d.action!,
    }));
  // Then: every failing sub-metric (matches Scorecard + Section 6 register)
  const actionForSubMetric = (dimId: string, label: string): string => {
    if (dimId === 'bed')        return 'Aerate to oxidise the bed interface; verify against the bed-health targets in Section 9.1.';
    if (dimId === 'oxygen')     return 'Increase Q_air per diffuser, add devices, or reduce SOD via NOM management; sustain DO > 4 mg/L at the interface.';
    if (dimId === 'algae')      return 'Break stratification through aeration; target chl-a < 30 ug/L and cyano fraction < 25 % summer mean.';
    if (dimId === 'nutrients')  return 'Combine internal P-release suppression (aeration / capping) with catchment P management; verify against WFD type-LM targets.';
    if (dimId === 'compliance') return 'Notify WTW operations of forecast PCV breach; plan PAC dosing or pre-treatment as required by DWI Reg 4(2)(a).';
    if (dimId === 'operational' && label.indexOf('OTE') >= 0)      return _isPreNda ? 'Adjust aeration configuration; engineering envelope provided post-NDA (Section 4.1).' : 'Specify finer-bubble diffuser or increase release depth (refer to post-NDA Section 4.1 for the recommended bubble-size envelope).';
    if (dimId === 'operational' && label.indexOf('Surface mixing') >= 0) return _isPreNda ? 'Adjust aeration configuration; engineering envelope provided post-NDA (Section 4.1).' : 'Add diffuser modules to extend hydrodynamic coverage above 80 % basin.';
    if (dimId === 'operational' && label.indexOf('footprint') >= 0)      return _isPreNda ? 'Adjust aeration configuration; engineering envelope provided post-NDA (Section 4.1).' : 'Re-space hex grid - footprint outside the 1.5-15 % typical band.';
    if (dimId === 'operational' && label.indexOf('Bubble') >= 0)         return _isPreNda ? 'Adjust aeration configuration; engineering envelope provided post-NDA (Section 4.1).' : 'Replace diffuser type with fine-bubble (1.0-3.0 mm release diameter).';
    if (dimId === 'operational' && label.indexOf('Stratification') >= 0) return _isPreNda ? 'Adjust aeration configuration; engineering envelope provided post-NDA (Section 4.1).' : 'Increase total Q_air or duty cycle; target stratification breakdown < 10 d.';
    if (dimId === 'operational' && label.indexOf('kg O2') >= 0)          return _isPreNda ? 'Adjust aeration configuration; engineering envelope provided post-NDA (Section 4.1).' : 'Re-tender diffuser supply or switch to centralised blower; target < GBP 1.5 / kg O2.';
    return 'Review the corresponding Health Scorecard dimension (Section 2) and adjust the relevant module / input parameter.';
  };
  health.dimensions.forEach(d => {
    d.subMetrics.filter(m => m.status === 'warn' || m.status === 'critical').forEach(m => {
      consolidatedActions.push({
        priority: consolidatedActions.length + 1,
        origin: `Sub-metric - ${d.label} > ${m.label}`,
        status: m.status === 'critical' ? 'CRITICAL' : 'WARN',
        action: `${m.label} = ${_redactValue(m.label, m.value)} (target ${m.target}). ${actionForSubMetric(d.id, m.label)}`,
      });
    });
  });
  if (consolidatedActions.length === 0) {
    P(doc, 'No action items identified - every Scorecard dimension and every sub-metric is within engineering tolerance for the inputs assessed. Continue routine operational monitoring as documented in the long-term management plan (Section 6 of the engineering scoping report).', ctx, opts, { italic: true });
  } else {
    autoTable(doc, {
      startY: ctx.y,
      head: [['#', 'Origin', 'Severity', 'Action required']],
      body: consolidatedActions.map(a => [a.priority, a.origin, a.status, a.action]),
      theme: 'grid',
      headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
      bodyStyles: { fontSize: 8.5, cellPadding: 1.5, valign: 'top' },
      columnStyles: {
        0: { cellWidth: 10, halign: 'right', fontStyle: 'bold' },
        1: { cellWidth: 40, fontStyle: 'bold' },
        2: { cellWidth: 24, halign: 'center', fontStyle: 'bold' },
        3: { cellWidth: 'auto' },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 2) {
          const t = String(data.cell.raw ?? '');
          if (t === 'CRITICAL') data.cell.styles.textColor = COLOUR.crit;
          else if (t === 'WARN') data.cell.styles.textColor = COLOUR.warn;
        }
      },
      margin: { left: MARGIN_X, right: MARGIN_X },
      didDrawPage: () => decoratePage(doc, opts, ctx),
    });
    ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  }
  ctx.y += 6;

  // ---------- 10.10 Final engineering assessment ----------
  ensureRoom(doc, ctx, opts, 50);
  H2(doc, '10.10  Final engineering assessment', ctx, opts);
  const verdictText = health.overallStatus === 'ok'
    ? 'On the basis of the parameters assessed and the active module configuration, the reservoir is judged to be in a HEALTHY operational state. The aeration scheme as specified in this scenario is meeting the engineering targets documented under Section 9.1 and the regulatory thresholds under Section 9.1. Continued monitoring against the KPIs in Section 10.8 is recommended, with annual revalidation through the platform.'
    : health.overallStatus === 'warn'
    ? 'On the basis of the parameters assessed and the active module configuration, the reservoir is judged to be AT RISK. One or more health dimensions (see Section 10.3) and / or compliance metrics (see Section 10.7) are below engineering tolerance. The action register at Section 10.9 documents the remedial measures required to restore healthy operation. These measures should be programmed into the AMP-8 / next operational cycle without further delay.'
    : 'On the basis of the parameters assessed and the active module configuration, the reservoir is judged to be in a CRITICAL operational state. One or more health dimensions and / or compliance metrics are materially outside engineering tolerance, indicating active regulatory non-compliance risk and / or imminent operational failure. The actions listed at Section 10.9 should be implemented as a matter of priority, with concurrent stakeholder notification (Water Undertaker technical lead, DWI as required under Regulation 28, and downstream WTW operations).';
  P(doc, verdictText, ctx, opts, { size: 9.5 });
  ctx.y += 2;
  P(doc, `Issued under Reynolds & Bauhm Smart Water platform version ${REPRODUCIBILITY.version} on ${new Date().toLocaleString()}. All methodology, equation provenance, validation evidence and platform limitations are documented in Sections 3 through 9 of this report.`,
    ctx, opts, { size: 8.5, italic: true, colour: COLOUR.muted });
  ctx.y += 4;

  // Independent reviewer sign-off block
  ensureRoom(doc, ctx, opts, 80);
  H2(doc, '10.11  Independent reviewer sign-off', ctx, opts);
  P(doc, 'This report is presented for independent technical assessment. The reviewing engineer is requested to confirm acceptance of each constituent element below before relying on the assessment for any external submission.',
    ctx, opts, { size: 8.5, italic: true, colour: COLOUR.muted });
  autoTable(doc, {
    startY: ctx.y,
    head: [['Element', 'Reviewer assessment (Y / N / N/A)', 'Reviewer initials & date']],
    body: [
      ['Section 2 - Health scorecard reviewed',                      '', ''],
      ['Section 3 - Input parameters verified against site data',    '', ''],
      ['Section 4 - Pipeline calculations spot-checked',             '', ''],
      ['Section 5 - Module placement confirmed appropriate',         '', ''],
      ['Section 6 - Improvement actions accepted / amended',         '', ''],
      ['Section 7 - Module activation matrix appropriate',           '', ''],
      ['Section 8 - Validation evidence reviewed',                   '', ''],
      ['Section 9.2 - Calibration out-of-range modules acknowledged','', ''],
      ['Section 10.7 - Regulatory compliance position accepted',     '', ''],
      ['Section 10.9 - Action register transferred to risk register','', ''],
      ['Final verdict (Section 10.10) ACCEPTED / REJECTED',          '', ''],
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOUR.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 9, cellPadding: 2.5, minCellHeight: 8 },
    columnStyles: {
      0: { cellWidth: 100, fontStyle: 'bold' },
      1: { cellWidth: 30, halign: 'center' },
      2: { cellWidth: 'auto' },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: () => decoratePage(doc, opts, ctx),
  });
  ctx.y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y += 8;

  // ----- CLOSING / DISCLAIMER -----
  // Force a fresh page so the closing statement has full room and consistent formatting
  doc.addPage();
  ctx.pageNum += 1;
  decoratePage(doc, opts, ctx);
  ctx.y = MARGIN_TOP;
  H1(doc, '11. Closing statement, disclaimer & confidentiality', ctx, opts);
  // Bold-labelled section helper for the closing statement
  const closingPara = (label, body) => {
    ensureRoom(doc, ctx, opts, 18);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);
    doc.text(s(label), MARGIN_X, ctx.y);
    ctx.y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(s(body), CONTENT_W);
    const h = lines.length * 4.4;
    ensureRoom(doc, ctx, opts, h + 2);
    doc.text(lines, MARGIN_X, ctx.y);
    ctx.y += h + 5;
  };

  closingPara('Closing statement.',
    'This report has been prepared by Jacek Jeziorny on behalf of Reynolds & Bauhm Ltd as an engineering assessment of the subject reservoir aeration scheme. The work is anchored in the United Kingdom regulatory framework and synthesises peer-reviewed primary scientific literature, statutory drinking-water regulations enforced by the Drinking Water Inspectorate (DWI), Water Framework Directive obligations administered by the Environment Agency (EA) in England, Natural Resources Wales (NRW), the Scottish Environment Protection Agency (SEPA) and the Department of Agriculture, Environment and Rural Affairs (DAERA) in Northern Ireland, the economic outcome framework set by the Water Services Regulation Authority (Ofwat), and industry-standard cost models documented under the HM Treasury Green Book and the UK water industry AMP-8 cycle.');

  closingPara('Seasonal series and document continuity.',
    'This document forms one of four reports in the Barcombe annual-cycle assessment series; companion reports for Winter baseline, Spring transition, and Autumn turnover scenarios are currently in preparation and will be issued under the same document-numbering protocol to provide complete year-round coverage.');

  closingPara('Implementation pathway.',
    'Reynolds & Bauhm Ltd is available to support the project lifecycle from initial scoping through to post-commissioning revalidation, in alignment with established UK engineering and economic frameworks (HM Treasury Green Book, Ofwat PR19 / PR24 outcome-delivery framework, AMP-8 business-plan cycle) and applicable UK statutory texts (the Water Supply (Water Quality) Regulations 2016 enforced by the DWI; the Water Environment (Water Framework Directive) (England and Wales) Regulations 2017 administered by the EA and NRW; the Reservoirs Act 1975 as amended by the Flood and Water Management Act 2010 and overseen by the EA Reservoir Safety team; and DEFRA cyanobacterial-risk guidance). The supporting evidence base, peer-site library, and technical calibration material referenced in the body of this report are disclosed in full only under executed Non-Disclosure Agreement in the post-NDA Full Report.');

  closingPara('Geographic and project scope.',
    'Reynolds & Bauhm Ltd operates worldwide and delivers projects across a broad portfolio of water-engineering applications - including drinking-water reservoir aeration, raw-water source management, eutrophic lake restoration, recreational and amenity lakes, fishery and aquaculture waterbodies, cooling-water reservoirs, post-industrial pit lakes, hydropower headponds, urban stormwater retention basins, and impounded river reaches. The Reynolds & Bauhm Smart Water platform is applicable to any lake or reservoir assessment regardless of geography, catchment, climate zone, basin morphology or trophic state. Where instructed outside the United Kingdom, Reynolds & Bauhm Ltd applies the equivalent national regulatory framework in place of the UK roster cited above and adapts the report structure to the host-country statutory environment. Reynolds & Bauhm Ltd is available to deliver site-specific assessments for additional client reservoirs anywhere in the world on request, with the same defensibility chain and pre-NDA / post-NDA staging applied uniformly.');

  closingPara('Site-specific verification required.',
    'Predictions presented herein are model-derived and require site-specific verification before being used in any contractual, regulatory or financial decision; no model is a substitute for direct in-situ measurement at the subject reservoir. The recommendations and verdicts in this report should be reviewed by a chartered CWEM / CEnv / CIWEM Suitably Qualified Experienced Person (SQEP) before being relied upon for a Reservoirs Act 1975 Section 10 submission to the Environment Agency, an Ofwat AMP-8 outcome-delivery commitment, or any Drinking Water Inspectorate Regulation 28 notification under the Water Supply (Water Quality) Regulations 2016.');

  ensureRoom(doc, ctx, opts, 24);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(COLOUR.brand[0], COLOUR.brand[1], COLOUR.brand[2]);
  doc.text(s('Confidentiality and intellectual-property notice.'), MARGIN_X, ctx.y);
  ctx.y += 5;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);
  const confidText = doc.splitTextToSize(s('The contents of this report and the underlying methodology are the proprietary intellectual property of Reynolds & Bauhm Ltd and the named author. Distribution of this document is restricted to the named recipient and individuals strictly authorised by the copyright holder under written agreement. The recipient agrees not to share, reproduce, transmit, copy, store in any retrieval system, translate, disclose to any third party, or use any part of this report (or any data, design, or derivative work derived from it) outside the named project team without prior written authorisation from Reynolds & Bauhm Ltd. Unauthorised use constitutes copyright infringement and breach of confidence under the Copyright, Designs and Patents Act 1988 and the common law of confidence, and may attract civil and criminal remedies.'), CONTENT_W);
  doc.text(confidText, MARGIN_X, ctx.y);
  ctx.y += confidText.length * 3.9 + 8;

  // Issuance footer line
  ensureRoom(doc, ctx, opts, 14);
  doc.setDrawColor(COLOUR.primary[0], COLOUR.primary[1], COLOUR.primary[2]);
  doc.setLineWidth(0.4);
  doc.line(MARGIN_X, ctx.y, MARGIN_X + 70, ctx.y);
  ctx.y += 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(COLOUR.primary[0], COLOUR.primary[1], COLOUR.primary[2]);
  doc.text(s('Issued by Jacek Jeziorny  -  Reynolds & Bauhm Ltd, United Kingdom'), MARGIN_X, ctx.y);
  ctx.y += 5;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(COLOUR.muted[0], COLOUR.muted[1], COLOUR.muted[2]);
  doc.text(s('Document reference shown on the cover page and on every page footer. - End of report -'), MARGIN_X, ctx.y);
  ctx.y += 5;
  doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);

  // ----- SECOND PASS: page X of Y numbering -----
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    // White-out the old placeholder and re-stamp
    doc.setFillColor(COLOUR.primary[0], COLOUR.primary[1], COLOUR.primary[2]);
    doc.rect(PAGE_W - 70, 9.5, 60, 3.5, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(s(`Page ${p} of ${totalPages}`), PAGE_W - MARGIN_X, 12, { align: 'right' });
    doc.setTextColor(COLOUR.ink[0], COLOUR.ink[1], COLOUR.ink[2]);
  }

  // Save
  const safe = opts.scenarioName.replace(/[^a-zA-Z0-9-_]+/g, '-').slice(0, 60);
  const modeSlug = reportMode === 'pre-nda' ? 'PRE-NDA' : 'FULL';
  doc.save(`rb-aeration-${modeSlug}-${safe}-${new Date().toISOString().slice(0, 10)}.pdf`);
}

/**
 * Map the human stage label back to module IDs in the train so we can pull
 * the right equations for each stage.
 */
function stageModulesFor(stageLabel: string, train: ModuleTrain): string[] {
  // Train.stages uses the same labels we render; find the matching stage block.
  const block = train.stages.find(st => normaliseStage(st.stage) === normaliseStage(stageLabel));
  return block ? block.moduleIds : [];
}

function normaliseStage(s: string): string {
  return s.replace(/\s+/g, ' ').trim().toLowerCase();
}
